import { ru } from './ru.js';

export const en = {
  phoneInput: {
    label: 'Phone',
    favorites: 'Favorites',
    changeCountry: 'Change country',
    search: {
      placeholder: 'Search country (name, code)',
      noResults: 'No matches',
    },
  },
  auth: {
    login: {
      footer: 'No access? Contact an administrator.',
      password: 'Password',
      pending: 'Signing in…',
      submit: 'Sign in',
      subtitle: 'Enter your credentials',
      title: 'Sign in',
    },
    password: {
      copied: 'Copied',
      copy: 'Copy',
      copyFailed: 'Failed to copy',
      generate: 'Generate password',
      revealToggle: 'Show/hide',
    },
  },
  common: {
    a11y: {
      skipToContent: 'Skip to content',
    },
    actions: {
      activeOnly: 'Active only',
      archive: 'Archive',
      back: 'Back',
      cancel: 'Cancel',
      close: 'Close',
      contact: 'Contact',
      confirm: 'Confirm',
      create: 'Create',
      delete: 'Delete',
      deactivate: 'Deactivate',
      edit: 'Edit',
      goHome: 'Home',
      more: 'More',
      next: 'Next',
      reactivate: 'Reactivate',
      reload: 'Reload',
      rename: 'Rename',
      resetPassword: 'Reset password',
      restore: 'Restore',
      retry: 'Retry',
      save: 'Save',
      select: 'Select',
      showArchive: 'Show archive',
      uploadFile: 'Upload file',
    },
    confirm: {
      title: 'Please confirm',
    },
    filters: {
      allPrefix: 'All: {{label}}',
      rangeMax: 'to',
      rangeMin: 'from',
    },
    fields: {
      optional: 'Optional',
      reasonPlaceholder: 'Reason…',
    },
    locales: {
      en: 'English',
      ru: 'Русский',
      zhHans: '简体中文',
    },
    pagination: {
      pageOfTotal: 'Page {{page}} of {{pages}} · {{total}} total',
    },
    dataTable: {
      sortBy: 'Sort by',
      sortDirection: {
        asc: 'Ascending',
        desc: 'Descending',
      },
    },
    combobox: {
      noResults: 'No results',
    },
    states: {
      emptyTitle: 'Empty',
      errorText: 'Could not load data.',
      errorTitle: 'Error',
      forbiddenText: 'You do not have permission to view this page.',
      forbiddenTitle: 'No access',
      loadError: 'Could not load',
      loading: 'Loading…',
      minimumReason: 'Minimum 3 characters',
      restoringSession: 'Restoring session…',
    },
    status: {
      active: 'Active',
      activeFeminine: 'Active',
      archive: 'Archive',
      archived: 'Archived',
      closed: 'Closed',
      draft: 'Draft',
      inWork: 'In work',
      inactive: 'Inactive',
      passwordResetRequired: 'Password change required',
      processed: 'Processed',
      return: 'Returned',
      system: 'System',
    },
    units: {
      byte: 'B',
      cubicMeterShort: 'm³',
      dayShort: 'd',
      hourShort: 'h',
      kilogramShort: 'kg',
      kilobyte: 'KB',
      megabyte: 'MB',
      minuteShort: 'min',
    },
    unsavedChanges: {
      title: 'Unsaved changes',
      body: 'There are unsaved changes. Continue editing or close without saving?',
      continueEditing: 'Continue editing',
      closeWithoutSaving: 'Close without saving',
    },
  },
  shell: {
    appError: {
      text: 'An unexpected error occurred. Try reloading the page.',
      title: 'Something went wrong',
    },
    language: {
      label: 'Language',
    },
    moduleError: {
      text: 'Could not render this page.',
      title: 'Module error',
    },
    nav: {
      groups: {
        admin: 'Administration',
        crm: 'CRM',
        dashboards: 'Dashboards',
        finance: 'Finance',
        logistics: 'Logistics',
        refs: 'Reference data',
        work: 'Work',
      },
      items: {
        agent: 'Agent',
        myDashboards: 'My dashboards',
        arrivalCities: 'Arrival cities',
        audit: 'Audit log',
        calls: 'Call center',
        cargo: 'Cargo',
        cargoProblems: 'Cargo: problems',
        carriers: 'Carriers',
        chinaRates: 'China rates',
        chinaRequestsDashboard: 'China requests',
        clients: 'Clients',
        commercialProposals: 'Commercial proposals',
        logisticsCalculations: 'Waybills',
        crmIntakeAdmin: 'Call center & duty',
        deals: 'Deals',
        depts: 'Departments',
        leads: 'Leads',
        payments: 'Payment requests',
        prices: 'Price lists',
        queues: 'Queues',
        revenue: 'Revenue',
        roles: 'Roles',
        russiaCarriers: 'RU Carriers',
        russiaRates: 'Russia rates',
        sla: 'SLA control',
        sources: 'Sources',
        stages: 'Stages',
        tasks: 'Tasks',
        transportTypes: 'Transport types',
        roadOptions: 'Roads',
        packagingOptions: 'Packaging',
        users: 'Users',
        warehouses: 'Warehouses',
        warehousesChina: 'China warehouses',
      },
    },
    notFound: {
      text: 'The page does not exist.',
      title: 'Not found',
    },
    placeholder: {
      text: 'This page will appear in the next migration phase.',
      title: 'Coming soon',
    },
    profile: {
      changePassword: 'Change password…',
      logout: 'Log out',
      menu: 'Profile menu',
      profile: 'Profile',
      settings: 'Settings',
    },
    sidebar: {
      collapse: 'Collapse',
      collapseMenu: 'Collapse menu',
      expand: 'Expand',
      expandMenu: 'Expand menu',
    },
    topbar: {
      menu: 'Menu',
      onShift: {
        label: 'On shift',
        ariaOn: 'On shift — tap to go off shift',
        ariaOff: 'Off shift — tap to go on shift',
      },
    },
    bell: {
      button: 'Notifications',
      loading: 'Loading…',
      loadError: 'Could not load notifications',
      empty: 'No notifications',
      unreadHeader: 'Unread',
      recentHeader: 'Recent',
      unreadDescription: {
        chat: '{{count}} new chat messages',
        rate: '{{count}} new carrier responses',
        rejection: '{{count}} carrier rejections',
        insurance: '{{count}} insurance events',
        responsibleStatus: 'Responsible changed',
        leadCallback: 'Client callback requests: {{count}}',
        cargoProblem: 'Cargo problem events: {{count}}',
      },
      recentDescription: {
        chat: 'Chat',
        rate: 'Carrier responses',
        rejection: 'Carrier rejections',
        insurance: 'Insurance',
        responsibleStatus: 'Responsible',
        leadCallback: 'Client requested a callback',
        cargoProblem: 'Cargo problem',
      },
    },
    messages: { button: 'Messages' },
    notificationsRow: {
      aria: {
        dot: 'Has unread notifications',
        count_one: '{{count}} unread notification',
        count_few: '{{count}} unread notifications',
        count_many: '{{count}} unread notifications',
        count_other: '{{count}} unread notifications',
        countCapped: '{{count}}+ unread notifications',
      },
    },
    workspaceTab: {
      aria: {
        notifications: {
          count_one: '{{count}} unread notification',
          count_few: '{{count}} unread notifications',
          count_many: '{{count}} unread notifications',
          count_other: '{{count}} unread notifications',
          countCapped: '{{count}}+ unread notifications',
        },
      },
    },
  },
  settings: {
    title: 'Settings',
    theme: {
      heading: 'Theme',
      light: 'Light',
      dark: 'Dark',
    },
    language: {
      heading: 'Language',
    },
    notifications: {
      heading: 'Notifications',
      loading: 'Loading…',
      loadError: 'Could not load notification preferences',
      previewButton: 'Play',
      previewAriaLabel: 'Play sound preview',
      previewFailed: 'Sound preview failed',
      soundOption: 'Sound {{index}}',
      soundSelectLabel: 'Notification sound',
      groups: {
        chat: 'Chat messages',
        rates: 'Rates, rejections, insurance',
        responsibleStatus: 'Responsible status changes',
      },
    },
  },
  profile: {
    title: 'Profile',
    sections: {
      view: 'Personal info',
      edit: 'Edit',
    },
    fields: {
      name: 'Name',
      email: 'Email',
      phone: 'Phone',
      role: 'Role',
      department: 'Department',
      createdAt: 'Joined',
    },
    actions: {
      save: 'Save',
      changePassword: 'Change password',
    },
    toasts: {
      saved: 'Profile updated',
    },
    errors: {
      nameRequired: 'Enter a name',
      nameTooLong: 'Name is too long (max 200 characters)',
      invalidPhone: 'Enter the number in international format, e.g. +79991234567',
    },
    placeholders: {
      missing: '—',
    },
  },
  errors: {
    auth: {
      invalidCredentials: 'Invalid email or password',
      refreshInvalid: 'Session expired. Sign in again',
      refreshReused: 'Suspicious activity detected. Sign in again',
      tokenInvalid: 'Session expired. Sign in again',
      unauthorized: 'Session expired. Sign in again',
      wrongCurrentPassword: 'Current password is incorrect',
    },
    domain: {
      actorHasOpenAssignment:
        'User has open requests. Reassign them to another logist before this change',
      cannotModifySelf: 'You cannot edit or reset your own password',
      conflict: 'Data conflict. Refresh the page',
      forbidden: 'Insufficient permissions',
      gateFailed: 'Action conditions are not met',
      invalidTransition: 'Action is not allowed for the current status',
      noLogistAvailable: 'No logists are on shift. Wait until one comes back on shift',
      notFound: 'Object not found',
      permissionsStale: 'Permissions changed in another session. Refresh the role and try again',
      phoneAlreadyExists: 'This phone is already in use by another record',
      callCenterOwnDuplicate: 'You have already submitted a lead with this phone number',
      processNoAnsweredCarrier: 'Cannot mark as processed: no carrier has answered yet',
      reasonRequired: 'Provide a reason',
      requestItemMissingPhoto: 'Each item must have 1 photo',
      returnBlockedByAnsweredCarrier:
        'Cannot return: carriers have answered bids. Reset, reject, or remove them first',
      winnerRequired: 'Pick the winning bid',
      shiftCannotLeaveLast: 'You cannot leave the shift: you are the last logist on shift',
      storageForbidden: 'No access to this file',
      userEmailTaken: 'A user with this email already exists',
      userNotFound: 'User not found',
      validation: 'Check the entered data',
    },
    generic: {
      unavailable: 'Service unavailable. Try again',
    },
    rateLimit: {
      exceeded: 'Too many attempts. Wait a minute',
    },
  },
  rbac: {
    actions: {
      savePermissions: 'Save permissions',
    },
    groups: {
      accounts: 'Accounts',
      agent: 'AI agent',
      arrivalCities: 'Arrival cities',
      audit: 'Audit log',
      callCenterLeads: 'Call-center leads',
      calls: 'Calls',
      cargo: 'Cargo',
      cargoProblems: 'Cargo problems',
      carriers: 'Carriers',
      chat: 'Chats',
      clients: 'Clients',
      commercialProposals: 'Commercial proposals',
      logisticsCalculations: 'Logistics calculations',
      companies: 'Companies',
      dashboard: 'Dashboards',
      deals: 'Deals',
      departments: 'Departments',
      leads: 'Leads',
      packaging: 'Packaging',
      paymentClaims: 'Payment requests',
      payments: 'Payments',
      priceLists: 'Price lists',
      requests: 'Requests',
      roads: 'Roads',
      roles: 'Roles',
      russiaCarriers: 'RU Carriers',
      russiaRequests: 'Russia requests',
      sources: 'Sources',
      stages: 'Stages',
      tasks: 'Tasks',
      transitions: 'Request transitions',
      transportTypes: 'Transport types',
      users: 'Users',
      warehouses: 'Warehouses',
      warehousesChina: 'China warehouses',
    },
    permissions: {
      accounts: {
        manage: 'Manage accounts',
        read: 'View accounts',
      },
      agent: {
        admin: 'AI agent administration (prompts, debug, SQL)',
      },
      arrivalCities: {
        manage: 'Manage arrival cities',
        read: 'View arrival cities',
      },
      audit: {
        read: 'View audit log',
      },
      callCenterLeads: {
        readOwn: 'View own call-center leads',
        submit: 'Submit call-center leads',
      },
      calls: {
        manage: 'Manage calls',
        read: 'View calls',
      },
      cargo: {
        carrierPayment: {
          manage: 'Set cargo carrier payment status',
        },
        chinaPayment: {
          manage: 'Set cargo China payment amount',
        },
        clientPayment: {
          manage: 'Set cargo client payment status',
          quickManage: 'Quick-set cargo client payment as fully paid',
          review: 'Review client payment (Financier RF)',
        },
        clientPaymentDocs: {
          manage: 'Manage cargo client payment documents',
        },
        clientTariff: {
          manage: 'Set cargo client transportation tariff',
        },
        create: 'Create cargo',
        delete: 'Delete cargo',
        dictionaries: {
          manage: 'Manage cargo dictionaries',
          read: 'View cargo dictionaries',
        },
        import: 'Import cargo files',
        manage: 'Manage cargo',
        read: 'View cargo',
        scope: {
          all: 'See all cargo (lifts the owner-only scope)',
        },
        update: 'Update cargo',
      },
      cargoProblems: {
        assignee: { manage: 'Reassign cargo-problem assignee' },
        attachments: { delete: 'Delete published cargo-problem attachments' },
        create: 'Create cargo problems',
        read: 'View cargo problems',
        scope: { all: 'View all submitted cargo problems' },
        work: { china: 'Work China queue', russia: 'Work Russia queue' },
      },
      carriers: {
        manage: 'Manage carriers',
        read: 'View carriers',
      },
      chat: {
        manage: 'Manage chats',
        read: 'View chats',
      },
      clients: {
        manage: 'Manage clients',
        read: 'View clients',
      },
      commercialProposals: {
        manage: 'Manage commercial proposals',
        read: 'View commercial proposals',
      },
      logisticsCalculations: {
        manage: 'Manage logistics calculations',
        read: 'View logistics calculations',
      },
      companies: {
        manage: 'Manage companies',
        read: 'View companies',
      },
      dashboard: {
        read: 'View dashboards',
      },
      deals: {
        manage: 'Manage deals',
        read: 'View deals',
      },
      departments: {
        manage: 'Manage departments',
        read: 'View departments',
      },
      leads: {
        assign: 'Assign leads',
        manage: 'Manage leads',
        read: 'View leads',
        scope: {
          all: 'See all leads (lifts the owner-only scope)',
        },
      },
      packaging: {
        manage: 'Manage packaging',
        read: 'View packaging',
      },
      paymentClaims: {
        manage: 'Manage payment requests',
        read: 'View payment requests',
      },
      payments: {
        manage: 'Manage payments',
        read: 'View payments',
      },
      priceLists: {
        manage: 'Manage price lists',
        read: 'View price lists',
      },
      requests: {
        export: { bulk: 'Export request batches' },
        manage: 'Manage requests',
        read: 'View requests',
        scope: {
          all: 'See all requests (lifts the owner-only scope)',
        },
        transition: {
          archive: 'Request transition: archive',
          close: 'Request transition: close',
          process: 'Request transition: process',
          reopen: 'Request transition: reopen',
          return: 'Request transition: return',
          send: 'Request transition: send',
          unarchive: 'Request transition: restore from archive',
          success: 'Request transition: finalise success',
          refuse: 'Request transition: finalise refusal',
          postpone: 'Request transition: postpone',
          cannotTransport: 'Request transition: cannot transport',
        },
      },
      roads: {
        manage: 'Manage roads',
        read: 'View roads',
      },
      roles: {
        manage: 'Manage roles',
        read: 'View roles',
      },
      russiaCarriers: {
        manage: 'RU Carriers: manage',
        read: 'RU Carriers: read',
      },
      russiaRequests: {
        manage: 'Manage Russia requests',
        read: 'View Russia requests',
        scope: {
          all: 'See all Russia requests (lifts the owner-only scope)',
        },
        transition: {
          archive: 'Russia request transition: archive',
          close: 'Russia request transition: close',
          process: 'Russia request transition: process',
          reopen: 'Russia request transition: reopen',
          return: 'Russia request transition: return',
          send: 'Russia request transition: send',
          unarchive: 'Russia request transition: restore from archive',
          success: 'Russia request transition: finalise success',
          refuse: 'Russia request transition: finalise refusal',
          postpone: 'Russia request transition: postpone',
        },
      },
      sources: {
        manage: 'Manage sources',
        read: 'View sources',
      },
      stages: {
        manage: 'Manage stages',
        read: 'View stages',
      },
      tasks: {
        manage: 'Manage tasks',
        read: 'View tasks',
      },
      transportTypes: {
        manage: 'Manage transport types',
        read: 'View transport types',
      },
      users: {
        manage: 'Manage users',
        read: 'View users',
      },
      warehouses: {
        manage: 'Manage warehouses',
        read: 'View warehouses',
      },
      warehousesChina: {
        manage: 'Manage China warehouses',
        read: 'View China warehouses',
      },
    },
  },
  unreadBadge: {
    markedUnread: 'Marked as unread',
    count_one: '{{count}} unread message',
    count_few: '{{count}} unread messages',
    count_many: '{{count}} unread messages',
    count_other: '{{count}} unread messages',
    capped: 'More than {{count}} unread messages',
    attentionCount_one: '{{count}} problem needs attention',
    attentionCount_few: '{{count}} problems need attention',
    attentionCount_many: '{{count}} problems need attention',
    attentionCount_other: '{{count}} problems need attention',
    attentionCapped: 'More than {{count}} problems need attention',
  },
  requests: {
    actions: {
      archive: 'Archive',
      close: 'Close request',
      process: 'Mark as processed',
      processDisabledNoAnswered: 'Waiting for at least one carrier to answer',
      reopen: 'Reopen for revision',
      return: 'Return for revision',
      send: 'Send to work',
      unarchive: 'Restore',
      success: 'Finalise success',
      refuse: 'Finalise refusal',
      cannotTransport: 'Cannot transport',
      postpone: 'Postpone',
      duplicate: 'Duplicate',
      russiaRate: {
        button: 'Russia rate',
        tooltip: {
          disabledEmptyCargo: 'Add an item or a box to create a Russia-rate request.',
          openExisting: 'Open the existing Russia-rate request.',
          create: 'Create a Russia-rate request from this one.',
          creating: 'Creating Russia-rate request…',
          error: 'Failed to create Russia-rate request. Please try again.',
        },
        create: {
          title: 'Create a Russia-rate request',
          addressLabel: 'Delivery address',
          addressRequired: 'Specify the delivery address.',
          confirm: 'Create request',
          cancel: 'Cancel',
        },
      },
    },
    audit: {
      empty: 'No audit events',
    },
    boxRegroup: {
      action: {
        edit: 'Edit groups',
      },
      editor: {
        title: 'Reassign items to groups',
      },
      toast: {
        saved: 'Item groups updated',
        stale: 'The box was updated by someone else. Please refresh and retry.',
        overrideConflict:
          'Cannot save: a carrier has a per-group rate or insurance pinned to a group that would become empty.',
        failed: 'Failed to save groups. Please try again.',
      },
      conflicts: {
        title: 'Override conflicts',
        line: 'Group {{ordinal}} ({{domain}}): {{carriers}}',
        domain: {
          bid: 'rate',
          insurance: 'insurance',
        },
      },
    },
    export: {
      action: 'Download Excel',
      downloading: 'Preparing file…',
      error: 'Failed to download file',
      bulk: {
        enterSelection: 'Select',
        selectAll: 'Select all results',
        selectingAll: 'Selecting…',
        clear: 'Clear',
        exitSelection: 'Exit',
        open: 'Create file',
        title: 'Batch export',
        confirm: 'Create file',
        includeRateColumns: 'Add rate fields',
        selectedCount_one: '{{count}} request selected',
        selectedCount_few: '{{count}} requests selected',
        selectedCount_many: '{{count}} requests selected',
        selectedCount_other: '{{count}} requests selected',
        selectColumn: 'Request selection',
        selectRequest: 'Select request {{id}}',
        capReached: 'You can select no more than 20 requests.',
        tooMany: 'More than 20 requests match. Narrow the filters and try again.',
        selectAllError: 'Could not select all results. Try again.',
        missing: 'Requests not found or unavailable: {{ids}}.',
        oversized: 'Request {{id}} is too large to export.',
      },
    },
    funnel: {
      openLead: 'Open linked lead {{code}}',
      openDeal: 'Open linked deal {{code}}',
    },
    errors: {
      detailFetchFailed: 'Unable to load request details. Please try again.',
    },
    rowGone: 'The request is no longer available.',
    nextStep: {
      draft: {
        incomplete: 'Fill in the missing fields and send to work. {{count}} field(s) left.',
        ready: 'The request is ready — send it to work.',
      },
      return: {
        incomplete: 'Fix the missing fields and resend. {{count}} field(s) left.',
        ready: 'Changes are in — resend the request.',
      },
      draftReadyPending: 'The request is complete.',
      returnReadyPending: 'The return is complete.',
      readyForWork: 'Waiting for the logistician to pick up the request.',
      waitingAnswers: 'Waiting for carrier answers.',
      pickWinner: 'Carriers have answered — pick a winner.',
      pickWinnerPending: 'Carrier responses are in — awaiting winner selection.',
      winnerSelected: 'A winner has been selected.',
      postponed: 'Request postponed',
      success: 'The request closed successfully.',
      terminal: 'The request is finished. No action needed.',
      action: {
        send: 'Send now',
        pickWinner: 'Go to selection',
      },
    },
    carriers: {
      add: 'Add',
      addBid: 'Add bid',
      addBidTitle: 'New bid — {{name}}',
      bid: {
        validUntil: 'Valid until {{date}}',
        notActualSince: 'Not actual since {{date}}',
        validityDaysPlaceholder: 'Bid validity, days',
        validityHint:
          'Validity is a whole number of days between {{min}} and {{max}}. Default is {{defaultDays}}.',
        validityPreset: {
          thisWeek: 'Until end of this week',
          nextWeek: 'Until end of next week',
          days7: '7 days',
          days10: '10 days',
          days14: '14 days',
          manual: 'Manual input',
        },
      },
      addPlaceholder: 'Select carrier',
      answer: 'Answer',
      completedCount: '{{completed}} of {{total}} responded',
      answerTitle: 'Carrier answer — {{name}}',
      bestOffers: 'Best offers',
      bids: 'Bids',
      cheapest: 'Cheapest',
      columns: {
        carrier: 'Carrier',
        comment: 'Comment',
        conditions: 'Conditions',
        days: 'Days',
        price: 'Rate',
        sentAt: 'Sent',
        status: 'Status',
      },
      commentPlaceholder: 'Carrier comment',
      contact: 'Contact',
      daysMaxPlaceholder: 'Transit, days (max)',
      daysMinPlaceholder: 'Transit, days (min)',
      daysPlaceholder: 'Transit time, days (optional)',
      destinationSection: {
        titleFull: 'Carriers — {{name}}',
        titleShort: 'Carriers — {{short}}',
      },
      generalSection: {
        title: 'General rates',
      },
      errors: {
        carrierCityNotInRequest: 'Destination city is not in the request destinations.',
        carrierAlreadyInCity: 'This carrier is already added to this city.',
        carrierAlreadyInRequest: 'This carrier is already added to this request.',
      },
      insurance: {
        section: 'Insurance',
        noQuote: 'No insurance quote yet',
        addQuote: 'Add insurance %',
        addOverride: 'Add override',
        reconfirm: 'Reconfirm',
        needsReconfirm: 'Cargo value changed — please reconfirm',
        percentLabel: '%',
        percentValue: '{{percent}}%',
        amountLabel: 'Amount',
        cargoValueLabel: 'Cargo value',
        commentLabel: 'Comment',
        commentPlaceholder: 'Optional comment',
        primary: {
          cannotDeleteWithOverrides:
            'Cannot delete the primary quote while overrides exist. Remove the overrides first.',
        },
        scope: {
          request: { label: 'Whole request' },
          item: { label: 'Position: {{name}}' },
          box: { label: 'Box #{{n}}' },
          boxItem: { label: 'Item in Box #{{n}}: {{name}}' },
          boxGroup: { label: 'Group {{k}} in Box #{{n}}' },
        },
        scopePicker: {
          placeholder: 'Select target',
          required: 'Select a target',
          noOptions: 'No remaining scopes to override',
        },
        errors: {
          percentBounds: 'Percent must be between 0 and 100',
        },
      },
      daysShort: '{{days}} d',
      daysShortRange: '{{min}}–{{max}} d',
      editBidTitle: 'Edit bid — {{name}}',
      empty: 'No carriers invited yet',
      noBids: 'No bids yet',
      pickWinner: 'Choose winner',
      pricePerUnitPlaceholder: 'Price per unit',
      pricePlaceholder: 'Delivery price',
      reject: 'Reject',
      rejectPlaceholder: 'Rejection reason',
      rejectTitle: 'Carrier rejection — {{name}}',
      reset: 'Back to waiting',
      state: {
        answered: 'Answered',
        pending: 'Waiting',
        rejected: 'Rejected',
      },
      total: 'Total',
      unitKg: 'per kg',
      unitM3: 'per m³',
      unitPiece: 'per piece',
      unitSuffix: {
        kg: '/kg',
        m3: '/m³',
        piece: '/pc',
      },
      winner: 'winner',
    },
    chat: {
      aria: {
        read: 'Read by counterparty',
        sent: 'Sent',
      },
      empty: 'No messages yet',
      markUnread: 'Mark as unread',
      newDivider: 'New',
      placeholder: 'Message as {{name}}…',
      roles: {
        admin: 'admin',
        logist_cn: 'logistician CN',
        logist_ru: 'logistician RF',
        manager: 'manager',
      },
      send: 'Send',
      unmarkUnread: 'Clear unread mark',
    },
    closeModal: {
      confirm: 'Confirm closing',
      noWinner: 'A winner must be picked before closing — the server requires it.',
      pickWinner: 'Select a winner from answered carriers.',
      title: 'Close request — {{id}}',
      winnerText: 'The request will move to Closed. Winner: {{name}}.',
    },
    common: {
      attachments: 'Attachments',
      autoSave: 'Autosave',
      createdAt: 'Created {{date}}',
      days: '{{count}} d',
      density: 'Density, kg/m³',
      from: 'From',
      guest: 'Guest',
      items: 'Items',
      logist_cn: 'Logistician CN',
      manager: 'Manager',
      noName: '(untitled)',
      notAssigned: 'not assigned',
      route: 'Route',
      to: 'To',
      updatedAt: 'Updated',
      volume: 'Volume',
      weight: 'Weight',
    },
    draftDefaults: {
      from: 'China',
      to: 'Russia',
    },
    returnTemplates: {
      cancel: 'I want to cancel the request',
      addItems: 'Need to add items',
      removeItems: 'Need to remove items',
      adjustWeightVolume: 'Need to adjust weight / volume',
      attachPacking: 'Need to attach the packing list',
    },
    draft: {
      addItem: 'Add item',
      deleteDraft: 'Delete draft',
      deleteReturn: 'Delete return',
      duplicateItem: 'Duplicate',
      emptyItems: 'Add at least one cargo item',
      frozen: {
        banner: 'Draft saved on the server. To change anything, start over — or try sending again.',
      },
      fromPlaceholder: 'Origin city in China',
      itemDescriptionPlaceholder: 'Briefly describe the item, contents, or packing details',
      itemNamePlaceholder: 'Item name',
      newItemName: 'New item',
      photoAttached: 'Photo attached',
      photoAddMore: 'Add more photos',
      photoRemove: 'Remove photo',
      photoReplace: 'Replace photo',
      photoRetry: 'Retry',
      photoUpload: 'Upload photo',
      photoUploading: 'Uploading…',
      photoFromClipboard: 'Add photo from clipboard',
      ready: 'Ready to send to logist',
      photoDecodeFailed: 'The browser could not decode this image format: {{format}}.',
      photoEncodeFailed: 'The browser could not prepare this image for upload.',
      photoUploadFailed: 'Upload failed — try again',
      startOver: 'Start over',
      save: 'Save draft',
      saveReturn: 'Save return',
      saving: 'Saving…',
      savedReady: 'Saved',
      unsavedDraft: 'Draft not saved yet',
      unsavedChanges: 'Unsaved changes',
      confirmDiscard: 'This draft has unsaved changes. Close and discard them?',
      closeConfirm: {
        titleUnsaved: 'Save draft?',
        titlePersisted: 'Save draft changes?',
        bodyUnsaved: 'This draft has unsaved changes. Save and close, or close without saving?',
        bodyPersisted: 'This draft has unsaved changes. Save and close, or close without saving?',
        saveAndClose: 'Save and close',
        continueEditing: 'Continue editing',
        closeWithoutSaving: 'Close without saving',
        confirmCloseWithoutSaving: 'Confirm: close without saving?',
        discardArmedHint: 'This cannot be undone.',
        uploadingHint:
          'Wait for the photo upload to finish — save/close is unavailable until then.',
      },
      deleteConfirm: {
        confirm: 'Confirm: delete draft?',
        hint: 'This cannot be undone.',
        uploadingHint: 'Wait for the photo upload to finish.',
      },
      photoClipboard: {
        unsupported: 'Your browser does not support clipboard reading.',
        denied: 'Clipboard access denied.',
        empty: 'No supported image found in clipboard.',
        failed: 'Could not read image from clipboard.',
        unsupportedType: 'This image type is not supported.',
      },
      photoReplaceConfirm: {
        title: 'Replace photo?',
        body: 'This item already has a photo. Replace it with the clipboard image?',
        confirm: 'Replace',
        cancel: 'Cancel',
      },
      destinations: {
        label: 'Destination city/cities',
        empty: 'No city selected yet',
        add: 'Add',
        pickerPlaceholder: 'Pick a city',
        remove: 'Remove {{name}}',
      },
      deliveryAddress: {
        label: 'Delivery address',
        placeholder: 'City, street, building, warehouse or recipient contact',
      },
      pickupAddress: {
        label: 'Pickup address',
        placeholder: 'City, street, building, warehouse or sender contact',
      },
      roads: {
        label: 'Roads',
        empty: 'No road selected',
        add: 'Add',
        pickerPlaceholder: 'Pick a road',
        remove: 'Remove {{name}}',
        catalogEmptyHint: 'The catalog has no active values. Ask the China logist.',
        catalogEmptyLink: 'Go to catalog →',
      },
      packaging: {
        label: 'Packaging',
        empty: 'No packaging selected',
        add: 'Add',
        pickerPlaceholder: 'Pick a packaging',
        remove: 'Remove {{name}}',
        catalogEmptyHint: 'The catalog has no active values. Ask the China logist.',
        catalogEmptyLink: 'Go to catalog →',
      },
      dictionarySelections: {
        inactiveSuffix: '(inactive)',
      },
      container: {
        transportTypeLabel: 'Transport type',
        pickerPlaceholder: 'Pick a transport type',
        capacityHint: '{{volumeMin}}–{{volumeMax}} m³ · {{payloadMin}}–{{payloadMax}} t',
      },
      insurance: {
        section: 'Insurance',
        toggleLabel: 'Request insurance information',
        cargoValueLabel: 'Cargo value',
        cargoValuePlaceholder: 'E.g. 12000',
        cargoCurrencyLabel: 'Currency',
        cargoCurrencyPlaceholder: 'Pick a currency',
      },
      required: {
        deliveryAddress: 'Container delivery address',
        pickupAddress: 'Container pickup address',
        destinations: 'At least 1 destination city',
        from: 'From',
        insuranceTriplet: 'Cargo value and currency for insurance',
        item: 'At least 1 item',
        itemOrBox: 'Add at least 1 item or box',
        itemDescription: 'Description for every item',
        itemName: 'Name for every item',
        itemPhoto: 'Photo for every item',
        itemQuantity: 'Quantity for every item',
        itemVolume: 'Volume greater than 0 for every item',
        itemWeight: 'Weight greater than 0 for every item',
        boxItem: 'At least 1 item in every box',
        boxItemName: 'Name for every box item',
        boxItemDescription: 'Description for every box item',
        boxItemPhoto: 'Photo for every box item',
        boxItemQuantity: 'Quantity greater than 0 for every box item',
        name: 'Name',
        prefix: 'Fill in:',
        to: 'To',
        transportType: 'Transport type',
        weight: 'Weight greater than 0',
        volume: 'Volume greater than 0',
      },
      saved: 'Saved {{time}}',
      sections: {
        basics: 'Basics',
        items: 'Items · {{count}}',
        dictionarySelections: 'Roads and packaging',
        files: 'Files and photos',
      },
      containerTotals: {
        section: 'Weight and dimensions',
        weight: 'Weight, kg',
        volume: 'Volume, m³',
      },
      standardTotals: {
        section: 'Weight and dimensions',
        weight: 'Weight, kg',
        volume: 'Volume, m³',
      },
      table: {
        brand: 'Brand',
        description: 'Description',
        material: 'Material',
        name: 'Name',
        photo: 'Photo',
        quantity: 'Places',
        placesCount: 'Places',
        productQuantity: 'Product qty',
        volume: 'Volume, m³',
        weight: 'Weight, kg',
      },
      packages: {
        toggle: 'Enter dimensions and places',
        addPlace: 'Add place',
        removePlace: 'Remove place',
        place: 'Place {{index}}',
        widthCm: 'Width, cm',
        depthCm: 'Depth, cm',
        heightCm: 'Height, cm',
        weightKg: 'Weight per place, kg',
        count: 'Places count',
        perPackageVolume: 'Volume per place, m³',
        rowVolume: 'Row volume, m³',
        itemTotal: 'Item total',
        itemTotalWeight: 'Weight: {{value}} kg',
        itemTotalVolume: 'Volume: {{value}} m³',
        itemTotalQuantity: 'Places: {{value}}',
        hint: 'Item weight and volume are computed automatically from the places list.',
        emptyPlaces: 'Add at least one place',
        mobileSummary: '{{w}} × {{d}} × {{h}} cm · {{weight}} kg · {{count}} pcs',
      },
      boxes: {
        sectionTitle: 'Boxes',
        emptyHint: 'Add boxes with nested items here.',
        addBox: 'Add box',
        removeBox: 'Remove box',
        boxLabel: 'Box {{index}}',
        count: 'Count',
        weightKg: 'Weight per box, kg',
        volumeM3: 'Volume per box, m³',
        volumeMode: 'Volume mode',
        modeDirect: 'Enter directly',
        modeDimensions: 'From dimensions',
        widthCm: 'Width, cm',
        depthCm: 'Depth, cm',
        heightCm: 'Height, cm',
        contents: 'Box contents',
        contentsEmpty: 'Empty. Add an item.',
        addItem: 'Add item',
        removeItem: 'Remove item',
        itemName: 'Name',
        itemBrand: 'Brand',
        itemMaterial: 'Material',
        itemDescription: 'Description',
        itemQuantity: 'Quantity',
        itemPhoto: 'Item photo',
        itemPhotoMissing: 'No photo',
        groups: {
          action: {
            assign: 'Group',
            newGroup: '+ New group',
          },
          label: {
            group: 'Group {{ordinal}}',
          },
          tooltip: {
            cannotShipTogether: 'These items cannot ship together',
          },
        },
      },
      titleLabel: 'Request name',
      titlePlaceholder: 'E.g. Foshan plumbing, shipment 05/26',
      toPlaceholder: 'Delivery city',
      returnReasonBanner: {
        title: 'Request returned by the logist',
        bodyPrefix: 'Reason:',
      },
    },
    containerTotalsBackfill: {
      save: 'Save',
      saved: 'Saved',
      error: 'Save failed',
    },
    format: {
      justNow: 'just now',
      remaining: '{{value}} left',
      completedIn: 'Completed in {{value}}',
      overdue: 'Overdue by {{value}}',
      yesterdayAt: 'yesterday, {{time}}',
    },
    filters: {
      slaHot: 'Hot',
      slaOverdue: 'Overdue',
      unread: 'Unread',
      kindStandard: 'Consolidated',
      kindContainer: 'Containers',
      kindSimple: 'Simple',
    },
    list: {
      all: 'All',
      columns: {
        answers: 'Answers',
        cargo: 'Cargo',
        density: 'Density, kg/m³',
        manager: 'Manager',
        messages: 'Messages',
        name: 'Name',
        deadlines: 'Deadline',
        status: 'Status',
        updated: 'Updated',
        volume: 'Volume',
        weight: 'Weight',
      },
      row: {
        containerBadge: 'Container',
        containerDestination: 'To client address',
        createdPrefix: 'Created',
        densitySuffix: 'kg/m³',
        simpleBadge: 'Simple',
        updatedPrefix: 'Updated',
        volumeSuffix: 'vol.',
        weightSuffix: 'wt.',
      },
      answersBar: {
        tooltip: 'Total {{total}} · Tariff {{tariff}} · Refused {{rejected}} · Pending {{pending}}',
      },
      empty: 'No requests match the filter',
      filters: 'Filters',
      filterPanel: {
        title: 'Filters',
        dateRange: 'Created',
        dateFrom: 'From',
        dateTo: 'To',
        weightRange: 'Weight (kg)',
        volumeRange: 'Volume (m³)',
        rangeMin: 'Min',
        rangeMax: 'Max',
        manager: 'Manager',
        managerAll: 'All managers',
        managerSelectedCount_one: '{{count}} manager selected',
        managerSelectedCount_few: '{{count}} managers selected',
        managerSelectedCount_many: '{{count}} managers selected',
        managerSelectedCount_other: '{{count}} managers selected',
        clearAdvanced: 'Clear advanced',
      },
      loadMore: 'Load more',
      loadingMore: 'Loading…',
      loading: 'Loading…',
      error: 'Failed to load requests',
      newRequest: 'Complex request',
      newContainer: 'New container',
      newSimple: 'New simple request',
      searchPlaceholder: 'Search by ID or name',
      statusesToggle: 'Statuses',
      title: 'Logistics requests',
    },
    items: {
      empty: 'No items added',
    },
    photoGallery: {
      counter: 'Photo {{current}} of {{total}}',
      prev: 'Previous',
      next: 'Next',
      close: 'Close',
    },
    photo: {
      moreBadge: '+{{count}}',
    },
    pickWinnerModal: {
      confirm: 'Assign winner',
      empty: 'No carrier answers yet, so there is nobody to choose.',
      title: 'Choose winner — {{id}}',
    },
    successModal: {
      confirm: 'Finalise success',
      title: 'Success — {{id}}',
      winnerText: 'Winner — {{name}}.',
      noBids:
        'This request has no answered carriers. Wait for responses or reopen the request for a fresh logistics pass.',
      expiredWarning:
        'This bid expired {{date}}. Re-confirm pricing with the carrier before finalising.',
    },
    refuseModal: {
      confirm: 'Finalise refusal',
      title: 'Refusal — {{id}}',
      explanation: 'The request will move to Refused. Any pre-selected winner is cleared.',
      reason: {
        quoteResponseTooSlow: 'Carrier quote response too slow',
        priceNotAccepted: 'Client did not accept the price',
        clientUnresponsive: 'Client did not respond',
        other: 'Other',
      },
      commentLabel: 'Comment (optional)',
      commentPlaceholder: 'Refusal details for the commercial team',
      commentHint: 'Visible to the team; not written to the audit log',
    },
    refusalDetails: {
      reasonMissing: 'Reason not specified',
      title: 'Refusal reason',
    },
    returnModal: {
      explanation:
        'The request will return to Returned status. The manager will see the reason and make corrections.',
      presets: {
        missingDescription:
          'Product descriptions are missing: materials of manufacture, area of application;',
        weightVolume: 'Check the weight and volume data.',
      },
      presetsTitle: 'Reason templates',
      reasonLabel: 'Return reason',
      reasonPlaceholder: 'Describe what needs clarification or correction',
      submit: 'Return to manager',
      title: 'Return for revision — {{id}}',
    },
    cannotTransportModal: {
      title: 'Cannot transport — request {{id}}',
      explanation:
        'The request will move to the "Cannot transport" status. This is a terminal status: carriers and bids are kept, but the manager sees the request as closed on our side.',
      reasonLabel: 'Reason',
      reasonPlaceholder: 'Explain why we cannot transport this cargo',
      submit: 'Mark as cannot transport',
    },
    reopenModal: {
      explanation:
        'The request will return to In Work. The winner pick is cleared and the SLA window restarts so a fresh logistics pass can be taken.',
      reasonLabel: 'Reopen reason',
      reasonPlaceholder: 'Explain why this request needs another logistics pass',
      submit: 'Reopen for revision',
      title: 'Reopen request — {{id}}',
    },
    roleSubtitle: {
      admin: 'Full access: monitoring, reopen, reassignment',
      logist_cn: 'You process the active pool and manage rates',
      manager: 'You create requests and choose winners',
    },
    status: {
      archive: 'Archive',
      closed: 'Closed',
      draft: 'Draft',
      inWork: 'In work',
      processed: 'Processed',
      clientDecision: 'Decision?',
      readyForWork: 'Ready for work',
      return: 'Returned',
      success: 'Success',
      refused: 'Refused',
      postponed: 'Postponed',
      cannotTransport: 'Cannot transport',
    },
    store: {
      created: 'Request created',
      defaultDraftName: 'New request (draft)',
      returnChatPrefix: '⤴ Return: {{reason}}',
      transition: {
        archive: 'Archived',
        close: 'Request closed',
        process: 'Marked as processed',
        return: 'Returned for revision',
        send: 'Sent to work',
        unarchive: 'Restored from archive',
        success: 'Success finalised',
        refuse: 'Refusal finalised',
        postpone: 'Request postponed',
      },
      winnerSelected: 'Winner selected: {{name}}',
    },
    tabs: {
      archive: 'Archive',
      archiveCount: 'Archive ({{count}})',
      audit: 'Log',
      auditCount: 'Log ({{count}})',
      carriers: 'Carriers ({{count}})',
      chat: 'Chat ({{count}})',
      files: 'Files',
      filesCount: 'Files ({{count}})',
      items: 'Items ({{count}})',
      returnBanner: 'Return for revision',
    },
    archive: {
      empty: 'No returns yet',
      returnedAt: 'Returned: {{date}}',
      backToRequest: 'Back to request',
      snapshotTitle: 'Return snapshot',
      carriersEmpty: 'No carriers in this snapshot',
      loadError: 'Failed to load the archive',
    },
    table: {
      brand: 'Brand',
      description: 'Description',
      material: 'Material',
      photos: 'Photos',
      quantity: 'Places',
      placesCount: 'Places',
      productQuantity: 'Product qty',
      title: 'Name',
    },
    unavailable: {
      title: 'Request unavailable',
    },
    files: {
      add: 'Add file',
      uploading: 'Uploading…',
      usage: '{{count}} of {{max}} files',
      saveFirst: 'Save the draft first to attach files.',
      capReached: 'File limit for this request reached.',
      empty: 'No files attached yet',
      download: 'Download',
      remove: 'Remove',
      errors: {
        wrongType: 'This file type is not supported',
        tooLarge: 'File is too large',
        uploadFailed: 'Upload failed — please try again',
        downloadFailed: 'Failed to download file',
        listFailed: 'Failed to load the file list',
        removeFailed: 'Failed to remove the file',
      },
    },
  },
  cargoProblems: {
    title: 'Cargo: problems',
    actions: {
      back: 'Back',
      cancel: 'Cancel',
      close: 'Close',
      confirm: 'Confirm',
      create: 'Create problem',
      delete: 'Delete',
      edit: 'Edit',
      editAndResubmit: 'Edit and resubmit',
      evaluate: 'Evaluate',
      claim: 'Claim',
      reject: 'Reject',
      reassign: 'Reassign',
      retryPublish: 'Retry publication',
      save: 'Save',
      submit: 'Submit',
      submitReview: 'Submit for review',
      publish: 'Report problem',
    },
    list: {
      subtitle: '{{count}} problems',
      empty: 'No problems match these filters',
      capped: 'Showing the first 300 problems.',
      columns: {
        attention: 'Attention',
        cargo: 'Cargo',
        category: 'Category',
        description: 'Description',
        status: 'Status',
        assignee: 'Assignee',
        updatedAt: 'Updated',
        tariff: 'Tariff',
        dimensions: 'Weight / volume',
        carrier: 'Carrier',
      },
    },
    filters: {
      toggle: 'Filters',
      search: 'Search',
      searchPlaceholder: 'Description, comment, article or name',
      status: 'Status',
      category: 'Category',
      queue: 'Queue',
      author: 'Author',
      assignee: 'Assignee',
      carrier: 'Carrier',
      createdFrom: 'Created from',
      createdTo: 'Created to',
      updatedFrom: 'Updated from',
      updatedTo: 'Updated to',
      overdueOnly: 'Overdue reaction only',
    },
    statuses: {
      DRAFT: 'Draft',
      NEW: 'New',
      REJECTED: 'Rejected',
      IN_PROGRESS: 'In progress',
      REVIEW: 'Review',
      NOT_RESOLVED: 'Not resolved',
      PARTIALLY_RESOLVED: 'Partially resolved',
      RESOLVED: 'Resolved',
    },
    categories: {
      SUPPLIER: 'Supplier',
      CHINA_WAREHOUSE: 'China warehouse',
      CHINA_CARRIER: 'China carrier',
      RUSSIA_WAREHOUSE: 'Russia warehouse',
      RUSSIA_CARRIER: 'Russia carrier',
    },
    queues: { CHINA_LOGISTICS: 'China logistics', RUSSIA_LOGISTICS: 'Russia logistics' },
    events: {
      SUBMITTED: 'Problem submitted',
      REJECTED: 'Problem rejected',
      CLAIMED: 'Problem claimed',
      REVIEW_SUBMITTED: 'Solution submitted for review',
      EVALUATED: 'Solution evaluated',
      EDITED: 'Problem edited',
      RESUBMITTED: 'Problem resubmitted',
      COMMENT_ADDED: 'Comment added',
      FILE_UPLOADED: 'File attached',
      ASSIGNEE_CHANGED: 'Assignee changed',
      ATTACHMENT_DELETED: 'Attachment deleted',
    },
    summary: {
      label: 'Problem summary counters',
      status: 'Statuses',
      category: 'Categories',
      queue: 'Queues',
    },
    editor: {
      editEyebrow: 'Edit problem',
      createEyebrow: 'New problem',
      newTitle: 'Problem draft',
      cargo: 'Cargo',
      cargoSearch: 'Enter an article or name',
      category: 'Category',
      queue: 'Queue',
      description: 'Briefly describe what happened',
      contextSection: 'Cargo context',
      briefSection: 'Problem brief',
      ownershipSection: 'Direction and owner',
      attachmentsSection: 'Initial evidence',
      attachments: 'Attachments',
      assignee: 'Assignee',
      assigneePlaceholder: 'Select an active assignee',
      assigneeUnavailable: 'unavailable',
      assigneeUnavailableHelp: 'This assignee is no longer eligible. Select an active assignee.',
      privateDraft: 'Private draft saved. It remains visible only to you until publication.',
      privateDraftRetry:
        'Publication failed. The private draft and ready files are retained; retry when ready.',
    },
    validation: {
      cargoRequired: 'Select cargo',
      categoryRequired: 'Select a category',
      queueRequired: 'Select a queue',
      descriptionRequired: 'Enter a description',
      assigneeRequired: 'Select an active assignee',
      noteRequired: 'Enter a comment',
      outcomeRequired: 'Select an outcome',
    },
    detail: {
      problem: 'Problem',
      summary: 'Problem summary',
      provenance: 'Details',
      workCard: 'Current work',
      briefMaterials: 'Initial evidence',
      terminalReadOnly: 'This problem is complete. Its work card and history are read-only.',
      category: 'Category',
      queue: 'Queue',
      author: 'Author',
      assignee: 'Assignee',
      createdAt: 'Created',
      updatedAt: 'Updated',
      reactionDueAt: 'Reaction due',
      notApplicable: 'Not applicable',
      description: 'Description',
      cargo: 'Cargo',
      openCargo: 'Open {{cargo}} in the cargo registry',
      outcome: 'Outcome',
    },
    timeline: {
      title: 'Technical history',
      empty: 'No history yet',
      hint: 'Workflow events only: statuses, owners, material deletions.',
    },
    workspace: {
      conversation: 'Conversation',
      materials: 'Materials',
      loadOlder: 'Show earlier messages',
      empty: 'No messages yet',
      conversationError: 'Could not load the conversation.',
      retry: 'Retry',
      actionRequired: 'Action required',
      systemCard: 'Workflow event',
      assigneeChange: '{{from}} → {{to}}',
      attachmentDeleted: 'Attachment deleted. Reason: {{reason}}',
    },
    message: {
      label: 'Message',
      placeholder: 'Write a message',
      send: 'Send',
      sent: 'Message sent',
      reply: 'Reply',
      replyingTo: 'Reply: {{author}}',
      cancelReply: 'Cancel reply',
      replyUnavailable: 'The original message is unavailable',
      documentNeedsText: 'Add a short note for the document',
      attachmentsPending: 'Wait for the attachments to finish uploading',
      empty: 'Write a message or attach a file',
      tooLong: 'The message is longer than {{max}} characters',
    },
    attachments: {
      add: 'Attach files',
      remove: 'Remove {{name}}',
      retry: 'Retry upload of {{name}}',
      statusUploading: '{{name}}: uploading',
      statusReady: '{{name}}: ready',
      statusError: '{{name}}: upload failed',
      previous: 'Previous file',
      next: 'Next file',
      download: 'Download',
      noPreview: 'No preview available for this file type',
      previewError: 'Could not open the file',
    },
    library: {
      title: 'Materials',
      search: 'Search by file name',
      empty: 'No materials yet',
      filters: { ALL: 'All', MEDIA: 'Media', DOCUMENTS: 'Documents' },
      error: 'Could not load the materials.',
      jump: 'Show in conversation',
      jumpBrief: 'Show in the brief',
      brief: 'Initial brief',
      delete: 'Delete material',
      deleteTitle: 'Delete this material?',
      deleteText: 'The file stops opening for every participant. Participants see the reason.',
      deleteReason: 'Deletion reason',
      deleted: 'Material deleted',
      uploadedBy: '{{author}} · {{date}}',
    },
    files: {
      attach: 'Attach file',
      uploading: 'Uploading…',
      uploaded: 'File attached',
      selected: '{{count}} selected',
      selectedList: 'Selected attachments',
      remove: 'Remove {{name}}',
      errors: {
        count: 'You can attach up to 10 files',
        type: 'This file type is not supported',
        size: 'File is too large',
        name: 'File name is too long',
        upload: 'Could not attach the file',
      },
    },
    toasts: {
      created: 'Problem draft created',
      published: 'Problem reported',
      resubmitted: 'Problem edited and resubmitted',
      saved: 'Changes saved',
      transitioned: 'Problem status updated',
      reassigned: 'Responsible employee changed',
      deleted: 'Problem draft deleted',
    },
    reassign: {
      title: 'Change responsible employee',
      current: 'Current employee',
      currentUnavailableHelp:
        'The current employee is no longer eligible for this queue. Select an available employee.',
      next: 'New employee',
      loadError: 'Could not load eligible employees.',
      slaHelp: 'Reassignment starts a new response deadline.',
    },
    noteTitles: {
      label: 'Comment',
      reject: 'Rejection reason',
      submitReview: 'Solution description',
      evaluate: 'Solution evaluation',
    },
    delete: {
      title: 'Delete draft?',
      text: 'The draft and its history will be permanently deleted.',
    },
    dimensions: { weight: 'Weight', volume: 'Volume' },
    cargoUnavailable: 'Cargo data is no longer available',
    unassigned: 'Unassigned',
    overdue: 'Overdue',
  },
  cargo: {
    title: 'Cargo',
    loading: 'Loading…',
    error: 'Failed to load cargo',
    empty: 'No cargo yet',
    actions: {
      import: 'Import',
      add: 'Add',
      filters: 'Filters',
      resetFilters: 'Reset',
      delete: 'Delete cargo',
      reportProblem: 'Report a problem',
      createTtn: 'Create waybill',
    },
    ttnInternalCosts: {
      chinaPayment: 'China payment',
      warehouseDispatch: 'China warehouse dispatch',
      warehouseInspection: 'China warehouse inspection',
      warehousePackaging: 'China warehouse packaging',
    },
    mirror: {
      banner:
        'Read-only mirror mode: NextKargo is the source of truth during migration. Editing is disabled.',
    },
    list: {
      searchPlaceholder: 'Search by article',
      loadMore: 'Load more',
      loadingMore: 'Loading…',
    },
    filters: {
      all: 'All',
      status: 'Status',
      manager: 'Manager',
      carrier: 'Carrier',
      carrierPlaceholder: 'Exact name',
      pathFormat: 'Route',
      clientPaymentStatus: 'Client payment',
      carrierPaymentStatus: 'Carrier payment',
      weightRange: 'Weight (kg)',
      volumeRange: 'Volume (m³)',
      totalCostRange: 'Total cost',
      rangeFrom: 'From',
      rangeTo: 'To',
      dateType: 'Date type',
      dateFrom: 'Date from',
      dateTo: 'Date to',
      overdue: 'Overdue',
      hasRejectedDocument: 'Rejected document',
      hasRejectedDocumentHint: 'With a rejected document',
    },
    lifecycle: {
      in_transit: 'In transit',
      arrived_not_issued: 'Arrived in RF',
      issued: 'Issued',
    },
    activeFilters: {
      search: 'Search',
      dateRange: '{{range}}',
      from: 'from {{value}}',
      to: 'to {{value}}',
      clear: 'Clear filter “{{label}}”',
    },
    dateTypes: {
      china_ship: 'China ship date',
      arrival: 'Arrival date',
      issue: 'Issue date',
    },
    overdue: {
      '1plus': '1+ days',
      '7plus': '7+ days',
    },
    status: {
      issued: 'Issued',
    },
    cells: {
      noManager: 'No manager',
      deleteDisabled: 'Delete (soon)',
    },
    tariff: {
      saved: 'Tariff saved',
    },
    delete: {
      title: 'Delete cargo',
      text: 'Delete cargo “{{article}}”? It can be restored later.',
      toast: 'Cargo deleted',
    },
    forms: {
      addTitle: 'New cargo',
      editTitle: 'Cargo “{{article}}”',
      unset: 'Not set',
      fields: {
        article: 'Article',
        manager: 'Manager',
        carrier: 'Carrier',
        pathFormat: 'Route',
        status: 'Status',
        nameGoods: 'Goods',
        numberOfSeats: 'Seats',
        weight: 'Weight, kg',
        volume: 'Volume, m³',
        transportationTariff: 'Tariff',
        transportationTariffForClients: 'Client shipping tariff',
        costGoods: 'Goods price',
        insuranceCost: 'Insurance price',
        packagingCost: 'Packaging price',
        timeFromChina: 'China ship date',
        totalCost: 'Total shipping cost',
        addressTransportationCost: 'Address delivery',
        chinaPaymentAmount: 'Cargo receipt, China',
        chinaWarehouseDispatchAmount: 'Warehouse dispatch, China',
        chinaWarehouseInspectionAmount: 'Warehouse inspection, China',
        chinaWarehousePackagingAmount: 'Warehouse packaging, China',
        prr: 'LUO',
        tatCost: 'TAT payment',
        paymentToCarrierStatus: 'Paid to carrier',
        timeCargoArrivalToRf: 'Cargo arrival to RF date',
        timeCargoRelease: 'Cargo release date',
      },
      errors: {
        required: 'Required field',
        numeric: 'Enter a number',
        releaseDateRequired: 'Enter the release date',
      },
      toasts: {
        created: 'Cargo created',
        updated: 'Cargo updated',
      },
    },
    import: {
      title: 'Import cargo',
      format: 'Format',
      pickFiles: 'Choose files',
      process: 'Upload',
      processing: 'Processing…',
      removeFile: 'Remove file',
      fileLabel: 'File {{index}}',
      buckets: {
        created: 'Created',
        updated: 'Updated',
        warnings: 'Warnings',
        errors: 'Errors',
      },
      errors: {
        wrongExtension: 'Only .xlsx and .xls files are allowed',
        tooLarge: 'File exceeds 50 MB',
        nameTooLong: 'File name is too long',
        tooManyFiles: 'No more than {{max}} files per import',
      },
      toasts: {
        processed: 'Import processed',
      },
    },
    payment: {
      title: 'Paid by client — {{article}}',
      statusLabel: 'Payment status',
      noDocumentWarning: 'Attach at least one document to save the status.',
      save: 'Save status',
      toasts: {
        statusSaved: 'Payment status saved',
      },
      docCounts: 'Rejected: {{rejected}}, pending: {{pending}}, accepted: {{accepted}}',
      rejectedMarker: 'Has a rejected document',
      manager: {
        desiredStatusLabel: 'Desired status',
        desiredStatusPlaceholder: 'Choose status…',
        options: {
          paid: 'Paid',
          partially_paid: 'Partially paid',
        },
        submit: 'Submit for review',
        submitHint: 'Needs at least one document and a chosen status.',
        lockedHint: 'Status is committed by review. Change the documents to reopen review.',
        toasts: {
          submitted: 'Submitted for review',
        },
      },
      review: {
        decisionLabel: 'Decision',
        decision: {
          pending: 'Not reviewed',
          accepted: 'Accept',
          rejected: 'Reject',
        },
        currencyLabel: 'Currency',
        receivedRubLabel: 'Received, ₽',
        foreignAmountLabel: 'Amount in currency',
        exchangeRateLabel: 'Rate',
        rubPreviewLabel: 'Computed, ₽',
        total: 'Total accepted: {{amount}}',
        save: 'Save decisions',
        invalidAmount: 'Enter an amount as 1234.56.',
        toasts: {
          saved: 'Decisions saved',
        },
      },
      errors: {
        reviewNotOpen: 'Review is closed for this cargo. Refresh the page.',
        reviewIncomplete: 'Not all documents have been reviewed.',
        documentLocked: 'This document is already committed and cannot be changed.',
        documentUnknown: 'Document not found. Refresh the page.',
        noActiveDocument: 'No active documents to review.',
      },
      documents: {
        title: 'Payment documents',
        empty: 'No documents yet',
        error: 'Failed to load documents',
        download: 'Download',
        upload: 'Attach document',
        uploading: 'Uploading…',
        uploaderDeleted: 'User deleted',
        reviewStatus: {
          pending: 'Not reviewed',
          accepted: 'Accepted',
          rejected: 'Rejected',
        },
        confirmDelete: {
          title: 'Delete document',
          text: 'Delete document “{{name}}”?',
        },
        toasts: {
          uploaded: 'Document attached',
          deleted: 'Document deleted',
        },
        errors: {
          wrongType: 'Unsupported file type',
          tooLarge: 'File exceeds 25 MB',
          nameTooLong: 'File name is too long',
        },
      },
    },
    summary: {
      error: 'Failed to load totals',
    },
    totals: {
      count: 'Count',
      totalWeightKg: 'Weight, kg',
      totalVolumeM3: 'Volume, m³',
      totalPrr: 'LUO',
      totalTat: 'TAT payment',
      totalChinaPayment: 'Cargo receipt, China',
      totalChinaWarehouseAmount: 'Warehouse total, China',
    },
    indicators: {
      paidNotIssued: 'Paid, not issued',
      missingResponsibleManager: 'No manager',
      missingPathFormat: 'No route',
      noInsurance: 'No insurance',
      rejectedDocument: 'Rejected document',
      copy: 'Copy articles',
    },
    statistics: {
      toggle: 'Statistics',
      loading: 'Loading statistics…',
      error: 'Failed to load statistics',
      noData: 'No data',
      clientPaymentStatus: {
        not_paid: 'Not paid',
        partially_paid: 'Partially paid',
        paid: 'Paid in full',
        under_review: 'Under review',
      },
      kpi: {
        count: 'Total cargo',
        inTransit: 'In transit',
        arrivedNotIssued: 'Arrived, not issued',
        issued: 'Issued',
        withoutManager: 'No manager',
        weight: 'Total weight, kg',
        volume: 'Total volume, m³',
        chinaAmount: 'China amount',
        avgDaysInTransit: 'Avg days in transit',
      },
      delta: {
        increased: 'up {{percent}}%',
        decreased: 'down {{percent}}%',
        flat: 'no change',
      },
      sections: {
        lifecycle: 'Stages',
        indicators: 'Indicators',
        totals: 'Totals',
        payments: 'Payments',
        carriers: 'Carriers',
        managers: 'Managers',
        monthly: 'Monthly trend',
        distribution: 'Distribution',
        money: 'Monthly amounts',
        ratings: 'Ratings',
        routes: 'Routes',
      },
      lifecycle: {
        total: 'Total',
        inTransit: 'In transit',
        arrivedNotIssued: 'Arrived, not issued',
        issued: 'Issued',
        withoutManager: 'No manager',
      },
      payments: {
        client: 'Client payment',
        carrier: 'Carrier payment',
      },
      carriers: {
        carrier: 'Carrier',
        count: 'Count',
        weight: 'Weight, kg',
        volume: 'Volume, m³',
      },
      managers: {
        manager: 'Manager',
        unassigned: 'No manager',
        count: 'Count',
        weight: 'Weight, kg',
        volume: 'Volume, m³',
        inTransit: 'In transit',
        arrivedNotIssued: 'Arrived, not issued',
        issued: 'Issued',
      },
      monthly: {
        month: 'Month',
        count: 'Count',
      },
      pies: {
        clientPayments: 'Client payments',
        carriers: 'Carriers',
        lifecycle: 'Stages',
        other: 'Other',
        status: 'Status',
        stage: 'Stage',
        count: 'Count',
        empty: 'No data',
      },
      money: {
        title: 'China amount, ¥',
        month: 'Month',
        aria: 'China amount by month',
      },
      ratings: {
        managers: 'Top managers',
        carriers: 'Top carriers',
        avgDaysInTransit: 'Avg days in transit',
        count: 'Count',
      },
      routes: {
        route: 'Route',
        none: 'No route',
        count: 'Count',
        countAria: 'Cargo by route',
        share: 'Route shares',
      },
      presets: {
        thisMonth: 'This month',
        last30: 'Last 30 days',
        quarter: 'Quarter',
        ytd: 'Year to date',
      },
    },
    columns: {
      article: 'Article',
      totalCost: 'Total shipping cost',
      daysWithoutPayment: 'Days unpaid',
      manager: 'Manager',
      carrier: 'Carrier',
      pathFormat: 'Route',
      status: 'Status',
      nameGoods: 'Goods',
      numberOfSeats: 'Seats',
      weight: 'Weight, kg',
      volume: 'Volume, m³',
      density: 'Density, kg/m³',
      transportationTariff: 'Tariff',
      transportationTariffForClients: 'Client shipping tariff',
      costGoods: 'Goods price',
      insuranceCost: 'Insurance price',
      packagingCost: 'Packaging price',
      timeFromChina: 'China ship date',
      chinaPayment: 'Cargo receipt, China',
      chinaWarehouseDispatch: 'Warehouse dispatch, China',
      chinaWarehouseInspection: 'Warehouse inspection, China',
      chinaWarehousePackaging: 'Warehouse packaging, China',
      chinaWarehouseTotal: 'Total, China',
      addressTransportationCost: 'Address delivery',
      prr: 'LUO',
      tatCost: 'TAT payment',
      clientPaymentStatus: 'Paid by client',
      carrierPaymentStatus: 'Paid to carrier',
      timeCargoArrivalToRf: 'Cargo arrival to RF date',
      timeCargoRelease: 'Cargo release date',
      daysOnTheWay: 'Days in transit',
      delete: 'Delete',
    },
  },
  russiaRequests: {
    title: 'Russia requests',
    empty: 'No requests yet',
    loading: 'Loading…',
    error: 'Failed to load requests',
    actions: {
      newSimple: 'Simple',
      newComplex: 'Complex',
      duplicate: 'Duplicate',
      send: 'Send',
      return: 'Return',
      process: 'Mark processed',
      reopen: 'Reopen',
      close: 'Close',
      success: 'Success',
      refuse: 'Refuse',
      postpone: 'Postpone',
      archive: 'Archive',
      unarchive: 'Unarchive',
    },
    list: {
      searchPlaceholder: 'Search by name or ID',
      loadMore: 'Load more',
      loadingMore: 'Loading…',
    },
    filters: {
      status: 'Status',
      type: 'Type',
      manager: 'Manager',
      all: 'All',
    },
    columns: {
      id: 'ID',
      type: 'Type',
      status: 'Status',
      name: 'Name',
      manager: 'Manager',
      startCities: 'Start cities',
      endAddress: 'Delivery address',
      primaryBidSummary: 'Primary bid',
      primaryBidEmpty: '—',
      primaryBidAwaiting: 'Awaiting bids',
      primaryBidDays: '{{count}} d',
      primaryBidDaysRange: '{{min}}–{{max}} d',
      createdAt: 'Created',
    },
    startCities: {
      empty: '—',
      more: '+{{count}}',
    },
    types: {
      lead: 'Lead',
      warehouse: 'From warehouse',
      inTransit: 'In transit',
      urgent: 'Urgent',
    },
    editor: {
      title: 'New Russia request',
      editTitle: 'Edit request',
      cancel: 'Cancel',
      save: 'Create',
      saving: 'Creating…',
      editSave: 'Save',
      editSaving: 'Saving…',
      submitError: 'Failed to create the request. Check the fields and try again.',
      cards: {
        basics: 'Basics',
        endAddress: 'Delivery address',
        startCities: 'Start cities',
      },
      fields: {
        type: 'Type',
        name: 'Name',
        endAddress: 'Delivery address',
        startCities: 'Start cities',
      },
      errors: {
        nameRequired: 'Enter a name',
        endAddressRequired: 'Enter a delivery address',
      },
      autosave: {
        saving: 'Saving…',
        savedAt: 'Saved at {{time}}',
        error: 'Could not save draft',
      },
      startCities: {
        empty: 'No start cities picked',
        pickerPlaceholder: 'Pick a city…',
        add: 'Add',
        remove: 'Remove {{name}}',
      },
      positions: {
        title: 'Positions',
        empty: 'No positions yet',
        add: 'Add position',
        newPosition: 'New position',
        fields: {
          name: 'Name',
          brand: 'Brand',
          material: 'Material',
          description: 'Description',
          weightKg: 'Weight, kg',
          volumeM3: 'Volume, m³',
          quantity: 'Places',
          productQuantity: 'Item count',
          photos: 'Photos',
        },
      },
      totals: {
        title: 'Weight and dimensions',
        weight: 'Weight, kg',
        volume: 'Volume, m³',
      },
      files: {
        title: 'Files',
        add: 'Add file',
        uploading: 'Uploading…',
        usage: '{{count}} of {{max}} files',
        capReached: 'File limit for this request reached.',
        empty: 'No files attached yet',
        download: 'Download',
        remove: 'Remove',
        errors: {
          wrongType: 'This file type is not supported',
          tooLarge: 'File is too large',
          uploadFailed: 'Upload failed — please try again',
          listFailed: 'Failed to load the file list',
          removeFailed: 'Failed to remove the file',
        },
      },
    },
    drawer: {
      close: 'Close',
      editDraft: 'Complete draft',
      unavailable: {
        title: 'Request unavailable',
      },
      tabsComingSoon: 'This tab will land in a later slice of #706.',
      parentLink: '← China #{{id}}',
      transitionError: 'Could not change the status. Try again.',
      sendReadiness: {
        title: 'Request not ready to send',
        missingEndAddress: 'Specify the delivery address.',
        missingTotals: 'Specify the total weight and volume of the cargo.',
        missingItems: 'Add at least one complete position (name, description, weight, volume).',
      },
      returnBanner: {
        title: 'Returned for revision',
        body: 'Action needed: review and resend.',
      },
      confirm: {
        close: {
          title: 'Close request?',
          text: 'Closing finalises the request. This cannot be undone except via re-open by an admin.',
        },
        archive: {
          title: 'Archive request?',
          text: 'Archiving hides the request from the default list. You can unarchive it later.',
        },
        success: {
          title: 'Finalise as success?',
          text: 'The request will be marked successful against the selected winning bid. Pick a winner on the carriers tab before finalising.',
        },
        refuse: {
          title: 'Record a refusal?',
          text: 'The request will be closed as a customer refusal. You can add a detailed reason later.',
        },
      },
      reason: {
        return: {
          title: 'Return to draft',
          text: 'Add a short reason explaining what to revise.',
        },
        reopen: {
          title: 'Reopen request',
          text: 'Add a short reason explaining what to revise after processing.',
        },
      },
      info: {
        managerId: 'Manager (id)',
      },
      header: {
        createdAt: 'Created {{date}}',
        emptyAddress: '—',
      },
      tabs: {
        cargo: 'Cargo',
        cost: 'Cost',
        chat: 'Chat',
      },
      cargo: {
        section: {
          body: 'Cargo',
        },
        totals: {
          weightKg: 'Total weight, kg',
          volumeM3: 'Total volume, m³',
          quantity: 'Total places',
        },
        files: {
          empty: 'No files',
          error: 'Failed to load files',
          columns: {
            name: 'Name',
            size: 'Size',
          },
        },
      },
      cost: {
        readonly: {
          empty: 'No cost yet',
          price: 'Cost',
          validityDays: 'Valid for, days',
          expiresAt: 'Expires',
          expired: 'expired',
        },
      },
      items: {
        empty: 'No items',
        columns: {
          name: 'Name',
          description: 'Description',
          brand: 'Brand',
          material: 'Material',
          productQuantity: 'Product qty',
          quantity: 'Places',
          weightKg: 'Weight, kg',
          volumeM3: 'Volume, m³',
          photos: 'Photos',
        },
      },
      boxes: {
        empty: 'No boxes',
        columns: {
          count: 'Count',
          weightKg: 'Weight, kg',
          volumeM3: 'Volume, m³',
        },
      },
      audit: {
        empty: 'No audit entries',
        error: 'Failed to load audit entries',
        columns: {
          at: 'When',
          action: 'Action',
          actor: 'Actor',
          transition: 'Transition',
        },
        actions: {
          CREATE: 'Create',
          UPDATE: 'Update',
          DELETE: 'Delete',
          TRANSITION_SEND: 'Send to logist',
          TRANSITION_AUTO_START: 'Auto-started',
          TRANSITION_RETURN: 'Return to manager',
          TRANSITION_PROCESS: 'Mark processed',
          TRANSITION_REOPEN: 'Reopened',
          TRANSITION_CLOSE: 'Close',
          TRANSITION_SUCCESS: 'Finalised as success',
          TRANSITION_REFUSE: 'Customer refusal',
          TRANSITION_POSTPONE: 'Request postponed',
          WINNER_PICK: 'Winner selected',
          TRANSITION_ARCHIVE: 'Archive',
          TRANSITION_UNARCHIVE: 'Unarchive',
          CHAT_POSTED: 'Chat message posted',
          CHAT_MARKED_UNREAD: 'Chat marked unread',
          CHAT_UNMARKED: 'Chat unmarked',
        },
      },
      carriers: {
        empty: 'No carriers assigned yet',
        error: 'Failed to load carriers',
        unitSuffix: {
          total: '',
          kg: '/kg',
          m3: '/m³',
          piece: '/pc',
        },
        states: {
          PENDING: 'Pending',
          ANSWERED: 'Answered',
          REJECTED: 'Rejected',
        },
        add: {
          modalTitle: 'Add carrier',
          trigger: 'Add carrier',
          submit: 'Add',
          modeInline: 'Enter new',
          modeFromCatalog: 'Pick from catalog',
          nameLabel: 'Carrier name',
          contactsLabel: 'Contacts',
          catalogPlaceholder: 'Pick a carrier from the catalog…',
          catalogError: 'Failed to load the carriers catalog',
          notFound: 'Carrier not found or deactivated',
          invalidStatus: 'Adding a carrier is allowed only in “In work” status',
          generic: 'Failed to add carrier',
        },
        bid: {
          priceLabel: 'Price',
          validityDaysLabel: 'Valid for, days',
          commentLabel: 'Comment',
          submit: 'Save bid',
          invalidStatus: 'Adding a bid is allowed only in “In work” status',
          notFound: 'Carrier not found',
          generic: 'Failed to save bid',
        },
        bidModal: {
          titlePrimary: 'Bid · {{name}}',
        },
        actions: {
          editCarrier: 'Edit',
          deleteCarrier: 'Delete',
          editBid: 'Edit bid',
          deleteBid: 'Delete bid',
          menuAria: 'Actions',
        },
        editCarrier: {
          title: 'Edit carrier {{code}}',
          submit: 'Save',
          invalidStatus: 'Editing is only available in “In work” status',
          notFound: 'Carrier not found',
          generic: 'Could not save changes',
        },
        editBid: {
          title: 'Edit bid',
        },
        deleteCarrier: {
          confirmTitle: 'Delete option {{code}}?',
          confirmBody: 'All bids for this carrier will be deleted. This is irreversible.',
        },
        deleteBid: {
          confirmTitle: 'Delete bid?',
          confirmBody: 'This is irreversible.',
        },
        card: {
          addPrimary: 'Add bid',
          addedAt: 'Added {{date}}',
          contactsHeading: 'Contacts',
          daysLeft: '{{days}} d left',
          expiresToday: 'Expires today',
          expired: 'Expired',
        },
        section: {
          bestOffers: 'Carriers',
          completedCount: '{{completed}} responded · {{total}} total',
        },
        winner: {
          pick: 'Select as winner',
          badge: 'Winner',
        },
        perCity: {
          heading: 'Rates by departure city',
          noRate: 'No rate yet',
          addRate: 'Add rate · {{carrier}}',
          editRate: 'Edit',
        },
      },
    },
    chat: {
      empty: 'No messages yet',
      placeholder: 'Reply as {{name}}…',
      send: 'Send',
      newDivider: 'New',
      markUnread: 'Mark unread',
      unmarkUnread: 'Clear unread',
      aria: {
        sent: 'Sent',
        read: 'Read by counterparty',
      },
    },
  },
  commercialProposals: {
    title: 'Commercial proposals',
    empty: 'No commercial proposals yet',
    loading: 'Loading…',
    error: 'Failed to load commercial proposals',
    actions: {
      create: 'New proposal',
      edit: 'Edit',
      preview: 'Preview',
      publish: 'Publish',
      publishing: 'Publishing…',
      revoke: 'Revoke',
      revoking: 'Revoking…',
      copyLink: 'Copy public link',
      copyLinkDone: 'Link copied',
      copyLinkError: 'Failed to copy the link',
      downloadPdf: 'Download PDF',
      downloadError: 'Failed to download the PDF',
    },
    columns: {
      publicCode: 'Code',
      client: 'Client',
      productName: 'Product',
      status: 'Status',
      owner: 'Owner',
      htmlViews: 'Views',
      updatedAt: 'Updated',
    },
    status: {
      draft: 'Draft',
      published: 'Published',
    },
    editor: {
      title: 'New proposal',
      editTitle: 'Edit proposal',
      cancel: 'Cancel',
      create: 'Create draft',
      creating: 'Creating…',
      createError: 'Failed to create the proposal. Check the fields and try again.',
      save: 'Save',
      saving: 'Saving…',
      saveError: 'Failed to save the proposal. Check the fields and try again.',
      publishedLocked:
        'A published proposal is read-only. Revoke it back to draft to make changes.',
      characteristicsHint: 'One characteristic per line',
      groups: {
        header: 'Header and rates',
        product: 'Product',
        buyout: 'Buyout',
        logistics: 'Logistics',
        totals: 'Totals',
        advanced: 'Conversion coefficients',
        notes: 'Notes',
        manager: 'Manager',
      },
      fields: {
        mode: 'Proposal composition',
        client: 'Addressee / client',
        productName: 'Product name',
        proposalDate: 'Date',
        cnyRateRub: 'CNY rate (₽ per 1 yuan)',
        usdRateRub: 'USD rate (₽ per 1 dollar)',
        buyoutTotalCurrency: 'Buyout total currency',
        logisticsTotalCurrency: 'Logistics total currency',
        grandTotalCurrency: 'Grand total currency',
        characteristics: 'Characteristics',
        buyoutTerm: 'Buyout term',
        goodsCny: 'Goods cost (¥)',
        chinaDeliveryCny: 'China delivery (¥)',
        paymentScheme: 'Payment scheme',
        destination: 'Destination',
        logisticsTerm: 'Logistics term',
        places: 'Places',
        weightKg: 'Weight (kg)',
        volumeM3: 'Volume (m³)',
        tariffUsd: 'Tariff ($)',
        tariffUnit: 'Tariff unit',
        packagingUsd: 'Packaging ($)',
        prrUsd: 'Handling ($)',
        servicePercent: 'Turnkey service (%)',
        noVatDivisor: 'Non-VAT %',
        vatDivisor: 'VAT %',
        insurancePercent: 'Insurance (%)',
        insuranceCnyPerUsdRate: 'CNY/USD rate for insurance',
        buyoutNotes: 'Notes — buyout',
        logisticsNotes: 'Notes — logistics',
        managerName: 'Name',
        managerPhone: 'Phone',
        photo: 'Product photo',
      },
      photo: {
        upload: 'Upload photo',
        replace: 'Replace photo',
        clear: 'Remove photo',
        uploading: 'Uploading…',
        alt: 'Product photo',
        fromClipboard: 'Add photo from clipboard',
        errors: {
          tooLarge: 'File is too large. Maximum size is 10 MB.',
          wrongType: 'SVG images are not supported.',
          decodeFailed: 'The browser could not decode this image format: {{format}}.',
          encodeFailed: 'The browser could not prepare this image for upload.',
          uploadFailed: 'Failed to upload the photo. Please try again.',
          unsupported: 'Your browser does not support clipboard reading.',
          denied: 'Clipboard access denied.',
          empty: 'No supported image found in clipboard.',
          unsupportedType: 'This image type is not supported.',
          failed: 'Could not read image from clipboard.',
        },
      },
      paymentScheme: {
        vat: 'Bank, with VAT + UPD',
        novat: 'Bank, no VAT + UPD',
        cash: 'Cash',
        crypto: 'Cryptocurrency',
        card: 'Card',
      },
      mode: {
        both: 'Buyout + Logistics',
        buyout: 'Buyout',
        logistics: 'Logistics',
      },
      tariffUnit: { kg: 'per kg', m3: 'per m³' },
    },
    calc: {
      title: 'Calculation',
      previewNote: 'Preview calculation. Final values are saved by the server.',
      backendNote: 'Server calculation',
      unavailable:
        'Calculation unavailable — check the rates and divisors (must be greater than zero).',
      serviceCny: 'Turnkey service',
      totalCny: 'Total in yuan',
      buyRub: 'Buyout (₽)',
      transportUsd: 'Transport',
      insuranceUsd: 'Insurance',
      logisticsUsd: 'Logistics (total)',
      logisticsRub: 'Logistics (₽)',
      grandTotal: 'Total: buyout + delivery',
      params: {
        title: 'Calculation parameters',
        appliedMarkup: 'Applied %',
        noMarkup: 'no markup applied',
      },
    },
    drawer: {
      loading: 'Loading…',
      error: 'Failed to load the proposal',
      ownerYou: 'You',
      previewTitle: 'Proposal preview',
      fields: {
        publicCode: 'Code',
        status: 'Status',
        client: 'Client',
        productName: 'Product',
        owner: 'Owner',
        htmlViews: 'Page views',
        pdfDownloads: 'PDF downloads',
        updatedAt: 'Updated',
        publishedAt: 'Published',
        photo: 'Product photo',
      },
    },
  },
  commercialProposalsPublic: {
    title: 'Commercial proposal',
    loading: 'Loading…',
    notFound: {
      title: 'Page unavailable',
      body: 'The link is invalid or has expired.',
    },
    characteristics: 'Characteristics',
    photoAlt: 'Product photo',
    downloadPdf: 'Download PDF',
    downloadError: 'Failed to download the PDF. Please try again.',
    fields: {
      proposalDate: 'Date',
      client: 'To',
      cnyRate: 'CNY rate',
      usdRate: 'USD rate',
    },
    totals: {
      buy: 'Buyout',
      logistics: 'Logistics',
      grand: 'Total',
    },
  },
  agent: {
    title: 'Agent',
    newConversation: 'New conversation',
    chat: {
      empty: 'Ask the analyst agent a question to get started.',
      thinking: 'Thinking…',
    },
    history: {
      title: 'Conversations',
      regionLabel: 'Agent conversation history',
      loading: 'Loading conversations…',
      empty: 'No saved conversations yet.',
      untitled: 'Untitled',
      loadFailed: 'Failed to load conversation history',
      openFailed: 'Failed to open conversation',
    },
    composer: {
      questionLabel: 'Question',
      placeholder: 'Ask about requests, revenue, conversion…',
      periodLabel: 'Period',
      periodAny: 'Any period',
      periods: {
        today: 'Today',
        last7d: 'Last 7 days',
        month: 'Current month',
        quarter: 'Quarter',
      },
      send: 'Send',
      errorRequired: 'Enter a question.',
      errorTooLong: 'The question is too long.',
    },
    progress: {
      running: 'in progress',
      done: 'done',
    },
    verdict: {
      showBasis: 'Show basis',
      hideBasis: 'Hide basis',
      basisLabel: 'Basis',
      candidateLabel: 'Candidate {{n}}',
      outcome: {
        agreed: 'Sources agree',
        reconciled: 'Reconciled',
        discrepancy: 'Sources disagree',
      },
      confidence: {
        high: 'High confidence',
        low: 'Low confidence',
      },
      source: {
        registry: 'Metric registry',
        sql: 'SQL query',
      },
      task: 'Task',
      taskId: 'Task ID',
      figures: 'Figures',
      noDataReasonLabel: 'Why no answer',
      noDataReason: {
        unsupported_domain: 'Unsupported analytics domain.',
        missing_schema: 'No approved schema or metric covers this question yet.',
        ambiguous_period: 'The requested period is ambiguous.',
        unsafe_columns: 'The query would need columns that are not approved for agent use.',
        too_broad: 'The question is too broad for one safe query.',
        query_rejected: 'The generated query was rejected by the safety gate.',
        query_timeout: 'The database query timed out.',
        missing_figure: 'The query did not return a required figure.',
        non_numeric_figure: 'A required figure was not numeric.',
        too_many_tasks: 'The request was split into too many tasks.',
        too_many_rows: 'The query returned too many rows to display safely.',
      },
      metric: 'Metric',
      sql: 'SQL',
      period: 'Period',
      filters: 'Filters',
      values: 'Values',
    },
    artifacts: {
      title: 'Artifacts',
      regionLabel: 'Agent artifacts',
      empty: 'Artifacts produced by the agent will appear here.',
      received: 'Artifact received',
      unsupported: 'This artifact type is not supported yet',
      pin: 'Pin',
      pinning: 'Pinning…',
      pinned: 'Artifact pinned to my dashboards.',
      pinFailed: 'Failed to pin artifact.',
      chart: {
        empty: 'No chart data',
        dataTable: 'Chart data',
        category: 'Category',
        noValue: '—',
        provenance: 'Source',
        createdAt: 'Created',
        tooltip: '{{series}} · {{x}}: {{y}}',
      },
    },
    savedDashboard: {
      title: 'My dashboards',
      regionLabel: 'Pinned artifacts',
      refresh: 'Refresh',
      retry: 'Retry',
      loading: 'Loading dashboards…',
      emptyTitle: 'No pinned artifacts yet',
      empty: 'Pin artifacts from agent answers to build a personal dashboard.',
      loadFailed: 'Failed to load my dashboards',
      loadFailedText: 'Try refreshing the page.',
      moveUp: 'Up',
      moveDown: 'Down',
      unpin: 'Remove',
      unpinned: 'Artifact removed from my dashboards.',
      unpinFailed: 'Failed to remove artifact.',
      reorderFailed: 'Failed to reorder.',
    },
    error: {
      notConfigured: 'The analyst agent is not configured. Contact an administrator.',
      sqlRejected: 'The database query was blocked by the safety rules.',
      sqlTimeout: 'The database query timed out. Try a narrower question.',
      runFailed: 'The model service failed to answer. Try again in a moment.',
      generic: 'The agent run failed. Try again.',
    },
    admin: {
      title: 'Agent settings',
      refresh: 'Refresh',
      loadFailed: 'Failed to load agent settings',
      activePrompt: 'Active prompt',
      glossaryCount: 'Terms',
      metricCount: 'Metrics',
      memoryState: 'Memory',
      enabled: 'Enabled',
      disabled: 'Disabled',
      catalogVersion: 'Catalog',
      promptDraft: 'Business prompt draft',
      promptPlaceholder: 'Business context, agent role, and answer style',
      savePromptDraft: 'Save draft',
      kindGlossary: 'Term',
      kindMetric: 'Metric',
      termPlaceholder: 'Name',
      definition: 'Definition',
      definitionPlaceholder: 'Business meaning',
      addDraft: 'Add draft',
      prompts: 'Prompt versions',
      glossary: 'Glossary',
      memory: 'Memory',
      memoryTitlePlaceholder: 'What to remember',
      memorySummary: 'Memory summary',
      memorySummaryPlaceholder: 'Fact, rule, or preference for future answers',
      addMemory: 'Add memory',
      saveMemory: 'Save memory',
      edit: 'Edit',
      cancel: 'Cancel',
      activate: 'Activate',
      approve: 'Approve',
      reject: 'Reject',
      archive: 'Archive',
    },
  },
  dashboards: {
    chinaRequests: {
      title: 'China requests — summary',
      periodLabel: 'Period',
      computedAt: 'updated {{time}}',
      error: 'Failed to load the summary',
      retry: 'Retry',
      periods: {
        today: 'Today',
        last7d: 'Last 7 days',
        month: 'Current month',
        quarter: 'Quarter',
        custom: 'Custom',
      },
      customFrom: 'From',
      customTo: 'To',
      customPrompt: 'Select a start and end date',
      kpi: {
        newRequests: 'New requests',
        inWorkNow: 'In work now',
        success: 'Successful',
        conversion: 'Conversion',
        overdueNow: 'Overdue now',
        volume: 'Volume',
        container: 'Container',
        nonContainer: 'Non-container',
        ppSuffix: ' pp',
      },
      delta: {
        increased: 'increased by {{value}}',
        decreased: 'decreased by {{value}}',
        flat: 'no change',
      },
      sla: {
        overdueNow: 'Overdue',
        hotNow: 'Hot',
        avgProcessingDays: 'Avg processing, days',
        reopenCount: 'Reopened to work',
        activeScopeNote:
          'Counts only active requests in the "Ready for work" and "In work" states. Processed requests are never counted as overdue.',
      },
      funnel: {
        title: 'Status funnel',
        statusHeader: 'Status',
        countHeader: 'Count',
      },
      trend: {
        title: 'Created vs closed',
        legend: {
          created: 'Created',
          closed: 'Closed',
          success: 'Successful',
        },
        empty: 'No data for the selected period',
        error: 'Failed to load the trend',
      },
      managers: {
        name: 'Manager',
        created: 'Created',
        inWorkNow: 'In work',
        success: 'Success',
        refused: 'Refused',
        conversion: 'Conversion',
        volume: 'Volume',
      },
      refusal: {
        title: 'Refusal reasons',
        reasonHeader: 'Reason',
        countHeader: 'Count',
      },
    },
  },
  clients: {
    title: 'Clients',
    emptyContacts: 'No active contacts',
    emptyLegal: 'No legal entities',
    emptyVersions: 'No version history',
    noPrimaryContact: 'No primary contact',
    versionsCollapsed: 'History loads when you open it.',
    actions: {
      create: 'Create client',
      addContact: 'Add contact',
      addLegal: 'Add legal entity',
      addRequisites: 'Add bank details',
      callContact: 'Call {{name}}',
      checkResult: 'Refresh and check',
      checkClientCreateResult: 'Refresh list and close',
      createAnyway: 'Create the set anyway',
      collapseText: 'Collapse',
      copyInnOf: 'Copy INN of {{name}}',
      copyPhoneOf: 'Copy phone of {{name}}',
      copyRequisites: 'Copy',
      copyRequisitesOf: 'Copy bank details “{{name}}” of {{entity}}',
      expandRequisitesOf: 'Show full bank details of {{name}}',
      expandText: 'Show all',
      setPrimary: 'Make primary',
      showArchive: 'Show archive ({{count}})',
      hideArchive: 'Hide archive',
      showHistory: 'Show history',
      hideHistory: 'Hide history',
      restoreVersion: 'Restore this version',
      viewVersion: 'View',
      viewVersionOf: 'View version #{{number}} of {{name}}',
      loadMore: 'Load more',
    },
    badges: {
      primary: 'Primary',
    },
    blocked: {
      archivePrimaryContact:
        'A primary contact cannot be archived: make another contact primary first.',
      archiveLastContact: 'This is the only active contact: add another one first.',
      alreadyPrimary: 'This contact is already the primary one.',
      pendingAction: 'The previous action on this record is still running.',
      pendingAncestor: 'Another change to this client is still running — wait for its result.',
      archivedClient:
        'This client is archived and cannot be changed. Restore it to edit contacts, legal entities and requisites again.',
      archivedLegal:
        'This legal entity is archived, so its requisites cannot be changed. Restore the entity to continue.',
    },
    fields: {
      name: 'Name',
      comment: 'Comment',
      contactFullName: 'Full name',
      contactComment: 'Contact comment',
      phone: 'Phone',
      primaryContact: 'Primary contact',
      legalEntities: 'Legal entities',
      manager: 'Manager',
      status: 'Status',
      inn: 'INN',
      legalType: 'Form',
      legalName: 'Legal name',
      requisites: 'Bank details',
      bankName: 'Bank',
      bik: 'BIC',
      account: 'Account',
      lifecycle: 'Stage',
      lengthCounter: '{{length}} of {{max}} characters',
    },
    hints: {
      innOoo: 'A company INN has 10 digits.',
      innIp: 'A sole proprietor INN has 12 digits.',
      legalTypeImmutable: 'The legal form cannot be changed after creation.',
      firstRequisites: 'The first bank details set. Add further sets from the client card.',
      requisitesDetails: 'The text is copied in full, line breaks included.',
      optionalSlot: 'Optional. An empty field means “not recorded”.',
      phoneFormat: 'International format, e.g. +79991234567.',
    },
    filters: {
      toggle: 'Filters',
      reset: 'Reset filters',
      search: {
        label: 'Search',
        placeholder: 'Name, phone, or INN',
        truncated: 'The query was shortened to {{max}} characters.',
      },
      archive: {
        label: 'Archive',
        active: 'Active',
        archived: 'Archived',
        all: 'All',
      },
      manager: {
        label: 'Manager',
        any: 'All managers',
        invalid:
          'The manager in the link was not recognised, so the list is not filtered by manager.',
      },
    },
    list: {
      loadedCount: 'Loaded: {{count}}',
      refreshing: 'refreshing…',
      anchorLost: 'Could not return to the row you opened: it is not in the current list.',
      appendFailed: 'The next page failed to load. The rows already loaded are kept.',
      refreshFailed: 'Could not refresh the list. You are looking at the rows loaded earlier.',
      stale: 'The rows below are the previous result — updating…',
    },
    detail: {
      refreshFailed:
        'Could not refresh this client. You are looking at the data loaded earlier; anything typed in open forms is kept.',
    },
    empty: {
      noResultsTitle: 'Nothing found',
      noResultsText: 'Change the query or reset the filters.',
      archiveTitle: 'The archive is empty',
      archiveText: 'There are no archived clients.',
      firstUseTitle: 'No clients yet',
      firstUseText: 'Create the first client to get started.',
    },
    copy: {
      success: 'Copied',
      failed: 'Copying failed. You can select the text manually.',
      fallbackTitle: 'Copying is unavailable',
      fallbackLabel: 'Text to copy',
    },
    requisites: {
      setOrdinal: 'Bank details #{{n}}',
      lastActiveHint: 'This is the only active set of bank details — it cannot be archived.',
      inspectTitle: 'Bank details currently on {{entity}}',
      inspectHelp:
        'This is the server state after the refresh. If the set you typed is not listed, save it again; if it is listed, close this editor.',
      inspectEmpty: 'This legal entity has no bank details sets.',
    },
    restore: {
      summary:
        'Restoring this version will change {{changed}} record(s), archive {{archived}}, bring {{restored}} back from the archive and re-create {{recreated}}.',
      renamedTo: '→ becomes “{{name}}”',
      changedTitle: 'Will be changed',
      archivedTitle: 'Will be archived',
      restoredTitle: 'Will be brought back',
      recreatedTitle: 'Will be re-created',
      rootTitle: 'Client fields',
      rootName: 'Name',
      rootComment: 'Comment',
      rootArchive: 'The client itself will be archived',
      rootUnarchive: 'The client itself will be un-archived',
      noChanges: 'The saved state matches the current one — nothing would change.',
      managerKept: 'The current owner and the client’s manager are kept as they are.',
      banksNotRecorded:
        'The snapshot stores only the requisites text, not bank name, BIC, account or the set history — those are not restored.',
      missingLegal:
        'The server will refuse this restore: legal entities recorded in the version no longer exist ({{names}}).',
      contactLabel: 'Contact “{{name}}”',
      legalLabel: 'Legal entity “{{name}}”',
      pending: 'Restoring… do not close this window.',
      impactUnavailable:
        'The client’s current state could not be loaded, so the list of effects cannot be shown. Only the saved version is below.',
    },
    conflict: {
      nameTaken: 'A client with this name already exists. Change the name.',
      phoneTaken: 'This phone is already taken by another record. Check the number.',
      innTaken: 'A legal entity with this INN already exists. Check the INN.',
      clientArchived: 'The client is archived. Restore it to make changes.',
      legalArchived: 'The legal entity is archived. Restore it to make changes.',
      requisitesArchived: 'This bank details set is archived. Restore it to make changes.',
      restoreAmbiguous:
        'This version cannot be restored: which bank details it belongs to is not determinable.',
      restoreAmbiguousDetail:
        'This version cannot be restored: which bank details it belongs to is not determinable. Nothing was changed. Check the legal entities ({{count}}) manually.',
      uncertainOutcome:
        'No response from the server — the record may have been saved. Do not submit again: refresh and check the result.',
      uncertainInspected:
        'The outcome of the first attempt is still unknown. Compare it with the refreshed list below: save again only if your set is missing.',
      noVerdictRetry: 'No response from the server. Nothing was lost — try saving again.',
      refreshFailed: 'The refresh failed. Your input is kept — try checking again.',
    },
    legalType: {
      OOO: 'OOO',
      IP: 'IP',
    },
    modals: {
      createTitle: 'New client',
      editTitle: 'Edit client',
      addContactTitle: 'Add contact: {{client}}',
      editContactTitle: 'Edit contact: {{client}}',
      addLegalTitle: 'Add legal entity: {{client}}',
      editLegalTitle: 'Edit legal entity: {{client}}',
      addRequisitesTitle: 'Add bank details: {{entity}}',
      editRequisitesTitle: 'Edit bank details: {{entity}}',
      versionTitle: 'Version #{{number}}',
    },
    sections: {
      summary: 'Overview',
      firstContact: 'First contact',
      contacts: 'Contacts',
      legalEntities: 'Legal entities',
      versions: 'Version history',
    },
    confirm: {
      archiveTitle: 'Archive client?',
      archiveMessage:
        'Client “{{name}}” will be archived. Child mutations are blocked until restore.',
      restoreTitle: 'Restore client?',
      restoreMessage: 'Client “{{name}}” will become editable again.',
      archiveContactTitle: 'Archive contact?',
      archiveContactMessage: 'Contact “{{name}}” will be moved to the archive.',
      primaryContactTitle: 'Make this contact primary?',
      primaryContactMessage: '“{{name}}” will become the client’s primary contact.',
      archiveLegalTitle: 'Archive legal entity?',
      archiveLegalMessage:
        'Legal entity “{{name}}” and its bank details will be archived and become read-only.',
      archiveRequisitesTitle: 'Archive bank details?',
      archiveRequisitesMessage:
        'Bank details “{{name}}” of {{entity}} will be archived. The text stays readable.',
      createAnywayTitle: 'Create a second set?',
      createAnywayMessage:
        'The outcome of the first attempt is unknown. If that set was saved, the legal entity ends up with an identical second one, which only a manager can remove by hand.',
      restoreVersionTitle: 'Restore this version?',
      restoreVersionMessage:
        'Client “{{name}}” will be replaced with version #{{number}}. The current state stays in the history.',
    },
    errors: {
      invalidPhone: 'Enter the number in international format, e.g. +79991234567',
      required: 'Fill this field in — spaces alone are not enough.',
      tooLong: 'The text is too long: keep it within {{max}} characters.',
      tooLongUnbounded: 'The text is too long.',
      innOoo: 'A company INN is 10 digits with a valid checksum.',
      innIp: 'A sole-proprietor INN is 12 digits with a valid checksum.',
      innAny: 'Enter a valid INN: 10 digits for a company, 12 for a sole proprietor.',
    },
    identifierTaken: {
      title: 'This contact is already in use',
      body: 'This contact is already taken by another record. Check the details or use a different contact.',
      bodyOwned:
        'This contact already belongs to an existing client. Open the client card to continue.',
      openClient: 'Open client',
    },
    toasts: {
      created: 'Client created',
      saved: 'Changes saved',
      archived: 'Client archived',
      restored: 'Client restored',
      contactAdded: 'Contact added',
      contactSaved: 'Contact updated',
      contactArchived: 'Contact archived',
      contactRestored: 'Contact restored',
      contactPrimary: 'Primary contact changed',
      legalAdded: 'Legal entity added',
      legalSaved: 'Legal entity updated',
      legalArchived: 'Legal entity archived',
      legalRestored: 'Legal entity restored',
      requisitesAdded: 'Bank details added',
      requisitesSaved: 'Bank details updated',
      requisitesArchived: 'Bank details archived',
      requisitesRestored: 'Bank details restored',
      versionRestored: 'Version restored',
    },
    versionAction: {
      create: 'Client created',
      update: 'Client updated',
      archive: 'Client archived',
      restore: 'Client restored',
      versionRestore: 'Restored from version',
      contactAdd: 'Contact added',
      contactUpdate: 'Contact updated',
      contactArchive: 'Contact archived',
      contactRestore: 'Contact restored',
      contactPrimary: 'Primary contact set',
      legalAdd: 'Legal entity added',
      legalUpdate: 'Legal entity updated',
      legalArchive: 'Legal entity archived',
      legalRestore: 'Legal entity restored',
      lifecycleWorking: 'Marked as a working client',
    },
    lifecycle: {
      potential: 'Potential',
      working: 'Active',
    },
  },
  carriers: {
    actions: {
      addPriceList: 'Add price list',
      addWarehouse: 'Add warehouse',
      attachFile: 'Attach file',
      create: 'Add carrier',
      open: 'Open',
      preview: 'Preview',
      replaceFile: 'Replace file',
      uploading: 'Uploading…',
    },
    columns: {
      contacts: 'Contacts',
      name: 'Name',
      notes: 'Comment',
      priceLists: 'Price lists',
      status: 'Status',
      warehouses: 'Warehouses',
    },
    empty: 'No carriers found',
    errors: {
      imageType: 'Upload a PDF, JPG, PNG, or WEBP file',
    },
    fields: {
      city: 'City',
      description: 'Description',
      email: 'Email',
      file: 'File',
      name: 'Name',
      noFile: 'No file selected',
      notes: 'Comment',
      optional: 'Optional',
      phone: 'Phone',
      priceListName: 'Price list name',
      warehouseName: 'Warehouse name',
    },
    filters: {
      active: 'Active',
      all: 'All',
      inactive: 'Inactive',
    },
    modals: {
      createTitle: 'New carrier',
      editTitle: 'Carrier — {{name}}',
    },
    search: 'Search',
    sections: {
      priceLists: 'Price lists',
      warehouses: 'Warehouses',
    },
    status: {
      active: 'Active',
      inactive: 'Inactive',
    },
    title: 'Carriers',
    toasts: {
      activated: 'Carrier activated',
      created: 'Carrier created',
      deactivated: 'Carrier deactivated',
      fileUploaded: 'File uploaded',
      priceListCreated: 'Price list added',
      priceListDeleted: 'Price list deleted',
      priceListUpdated: 'Price list updated',
      updated: 'Carrier updated',
      warehouseCreated: 'Warehouse added',
      warehouseDeleted: 'Warehouse deleted',
      warehouseUpdated: 'Warehouse updated',
    },
  },
  arrivalCities: {
    actions: {
      create: 'Add city',
    },
    badges: {
      default: 'Default',
    },
    columns: {
      default: 'Default',
      name: 'Name',
      shortName: 'Short',
      status: 'Status',
    },
    empty: 'No arrival cities found',
    errors: {
      shortNameRegex: 'Short name must be 1-3 Cyrillic or Latin letters or digits',
    },
    fields: {
      isDefault: 'Default arrival city',
      name: 'Name',
      shortName: 'Short name',
      shortNameHelp: 'Up to 3 characters: Cyrillic or Latin letters, digits',
    },
    filters: {
      active: 'Active',
      all: 'All',
      inactive: 'Archived',
    },
    modals: {
      createTitle: 'New arrival city',
      editTitle: 'Arrival city — {{name}}',
    },
    search: 'Search',
    status: {
      active: 'Active',
      inactive: 'Archived',
    },
    title: 'Arrival cities',
    toasts: {
      activated: 'City restored',
      archived: 'City archived',
      created: 'City created',
      updated: 'City updated',
    },
  },
  roadOptions: {
    actions: {
      create: 'Add road',
    },
    columns: {
      createdAt: 'Created',
      name: 'Name',
      sortOrder: 'Order',
      status: 'Status',
    },
    empty: 'No roads found',
    errors: {
      sortOrderInvalid: 'Order: integer ≥ 0',
    },
    fields: {
      name: 'Name',
      sortOrder: 'Sort order',
      sortOrderHelp: 'Integer ≥ 0; smaller values appear first',
    },
    filters: {
      active: 'Active',
      all: 'All',
      inactive: 'Archived',
    },
    hints: {
      archivedRow: 'Archived row — unavailable in new requests',
    },
    modals: {
      createTitle: 'New road',
      editTitle: 'Road — {{name}}',
    },
    search: 'Search',
    status: {
      active: 'Active',
      inactive: 'Archived',
    },
    title: 'Roads',
    toasts: {
      activated: 'Road restored',
      archived: 'Road archived',
      created: 'Road created',
      updated: 'Road updated',
    },
  },
  packagingOptions: {
    actions: {
      create: 'Add packaging',
    },
    columns: {
      createdAt: 'Created',
      name: 'Name',
      sortOrder: 'Order',
      status: 'Status',
    },
    empty: 'No packaging found',
    errors: {
      sortOrderInvalid: 'Order: integer ≥ 0',
    },
    fields: {
      name: 'Name',
      sortOrder: 'Sort order',
      sortOrderHelp: 'Integer ≥ 0; smaller values appear first',
    },
    filters: {
      active: 'Active',
      all: 'All',
      inactive: 'Archived',
    },
    hints: {
      archivedRow: 'Archived row — unavailable in new requests',
    },
    modals: {
      createTitle: 'New packaging',
      editTitle: 'Packaging — {{name}}',
    },
    search: 'Search',
    status: {
      active: 'Active',
      inactive: 'Archived',
    },
    title: 'Packaging',
    toasts: {
      activated: 'Packaging restored',
      archived: 'Packaging archived',
      created: 'Packaging created',
      updated: 'Packaging updated',
    },
  },
  russiaCarriers: {
    actions: {
      create: 'Add carrier',
    },
    columns: {
      contacts: 'Contacts',
      name: 'Name',
      status: 'Status',
    },
    empty: 'No carriers found',
    errors: {
      contactsRequired: 'Contacts are required',
      nameRequired: 'Name is required',
    },
    fields: {
      contacts: 'Contacts',
      contactsHelp: 'Phones, emails, messengers — one entry per line',
      name: 'Name',
    },
    filters: {
      active: 'Active',
      all: 'All',
      inactive: 'Archived',
    },
    hints: {
      archivedRow: 'Archived row — unavailable in new requests',
    },
    modals: {
      createTitle: 'New carrier',
      editTitle: 'Carrier — {{name}}',
    },
    search: 'Search',
    status: {
      active: 'Active',
      inactive: 'Archived',
    },
    title: 'RU Carriers',
    toasts: {
      activated: 'Carrier restored',
      archived: 'Carrier archived',
      created: 'Carrier created',
      updated: 'Carrier updated',
    },
  },
  transportTypes: {
    actions: {
      create: 'Add transport type',
    },
    category: {
      container: 'Container',
      truck: 'Truck',
    },
    columns: {
      category: 'Category',
      code: 'Code',
      name: 'Name',
      payload: 'Payload, t',
      sortOrder: '#',
      status: 'Status',
      volume: 'Volume, m³',
    },
    empty: 'No transport types found',
    errors: {
      payloadRange: 'Max. payload must be greater than or equal to min.',
      volumeRange: 'Max. volume must be greater than or equal to min.',
    },
    fields: {
      category: 'Category',
      code: 'Code',
      codeHint: 'Lowercase ASCII letters, digits, _, 2–64 characters. Immutable.',
      description: 'Description',
      nameEn: 'Name (EN)',
      nameRu: 'Name (RU)',
      payloadMax: 'Max. payload, t',
      payloadMin: 'Min. payload, t',
      sortOrder: 'Sort order',
      volumeMax: 'Max. volume, m³',
      volumeMin: 'Min. volume, m³',
    },
    filters: {
      active: 'Active',
      all: 'All',
      categoryAll: 'All categories',
      inactive: 'Archived',
    },
    modals: {
      createTitle: 'New transport type',
      editTitle: 'Transport type — {{name}}',
    },
    range: '{{min}} – {{max}}',
    search: 'Search',
    status: {
      active: 'Active',
      inactive: 'Archived',
    },
    title: 'Transport types',
    toasts: {
      activated: 'Transport type restored',
      archived: 'Transport type archived',
      created: 'Transport type created',
      updated: 'Transport type updated',
    },
  },
  sources: {
    title: 'Lead sources',
    actions: {
      create: 'Add source',
      rotate: 'Rotate secret',
      archive: 'Archive',
    },
    columns: {
      code: 'Code',
      displayName: 'Display name',
      status: 'Status',
      secretIssuedAt: 'Secret issued',
    },
    status: {
      enabled: 'Enabled',
      disabled: 'Disabled',
      archived: 'Archived',
    },
    filters: {
      active: 'Active and disabled',
      all: 'Include archived',
    },
    empty: {
      title: 'No lead sources yet',
      text: 'Create your first source to start receiving leads.',
    },
    form: {
      createTitle: 'New lead source',
      editTitle: 'Edit lead source',
      code: 'Code',
      codeHelp: 'Lowercase ASCII letters, digits, underscores. Immutable.',
      codePlaceholder: 'website_intake',
      codeShapeError: 'Code must match a-z, 0-9, _ (3-64 chars).',
      displayName: 'Display name',
      enabled: 'Enabled — accept incoming intake',
      create: 'Create',
      save: 'Save',
    },
    confirm: {
      rotate:
        'Rotating the secret invalidates the old one immediately. The new secret is shown only once. Continue?',
      archive: 'Archive this source? Incoming intake will return 410 Gone.',
    },
    reveal: {
      title: 'Lead source secret — shown once',
      warning:
        'Copy this secret now. It will never be shown again — rotation is the only way to recover access.',
      source: 'Source',
      secret: 'Secret',
      copy: 'Copy',
      copiedShort: 'Copied',
      copied: 'Secret copied to the clipboard.',
      copyFallback:
        'Clipboard write failed. Select the secret value and copy it manually before closing.',
      closeWithoutCopy: 'Close without copying',
    },
    toasts: {
      created: 'Source created. Copy the secret before closing the reveal.',
      updated: 'Source updated',
      rotated: 'Secret rotated. Copy the new secret before closing the reveal.',
      archived: 'Source archived',
    },
    logs: {
      title: 'Delivery logs — {{code}}',
      empty: {
        title: 'No delivery logs',
        text: 'Logs appear once the source receives an intake request.',
      },
      lead: 'lead {{id}}',
      idempotency: 'idempotency key {{key}}',
    },
  },
  stages: {
    title: 'Lead stages',
    hint: {
      lockedCatalog:
        'The seven canonical stages are locked. Only labels and task templates are editable.',
    },
    columns: {
      sortOrder: '#',
      code: 'Code',
      kind: 'Kind',
      displayName: 'Display label',
      taskTitle: 'Primary task title',
      defaultDueMinutes: 'Default due (min)',
      noTaskTitle: 'No primary task',
    },
    kind: {
      ACTIVE: 'Active',
      TERMINAL: 'Terminal',
    },
    empty: {
      title: 'Lead stages not yet seeded',
      text: 'Run the seed migration to populate the seven canonical stages.',
    },
    form: {
      editTitle: 'Edit lead stage',
      code: 'Code',
      kind: 'Kind',
      displayName: 'Display label',
      taskTitle: 'Primary task title',
      taskTitleClearHint: 'Leave empty to disable the auto-created task',
      defaultDueMinutes: 'Default due (minutes)',
      defaultDueClearHint: 'Leave empty for no due-time default',
      defaultDueRange: 'Must be an integer between {{min}} and {{max}}',
      save: 'Save',
    },
    toasts: {
      updated: 'Stage updated',
    },
  },
  warehouses: {
    columns: {
      carrier: 'Carrier',
      city: 'City',
      description: 'Description',
    },
    empty: 'No warehouses found',
    search: 'Search',
    title: 'Warehouses',
    toasts: {
      copied: 'Description copied',
      copyFailed: 'Could not copy description',
    },
  },
  priceLists: {
    columns: {
      carrier: 'Carrier',
      name: 'Price list',
      preview: 'File',
    },
    empty: 'No price lists found',
    fileLabel: {
      file: 'File',
      image: 'Image',
      pdf: 'PDF',
    },
    modalTitle: '{{carrier}} — {{name}}',
    search: 'Search',
    title: 'Price lists',
  },
  audit: {
    actorDeleted: '(deleted)',
    detailTitle: 'Event: {{action}}',
    empty: 'No records',
    events: {
      arrivalCityArchive: 'Arrival city archived',
      arrivalCityCreate: 'Arrival city created',
      arrivalCityRestore: 'Arrival city restored',
      arrivalCityUpdate: 'Arrival city updated',
      carrierCreate: 'Carrier created',
      carrierActivate: 'Carrier activated',
      carrierDeactivate: 'Carrier deactivated',
      carrierPriceListCreate: 'Carrier price list created',
      carrierPriceListDelete: 'Carrier price list deleted',
      carrierPriceListUpdate: 'Carrier price list updated',
      carrierUpdate: 'Carrier updated',
      carrierWarehouseCreate: 'Carrier warehouse created',
      carrierWarehouseDelete: 'Carrier warehouse deleted',
      carrierWarehouseUpdate: 'Carrier warehouse updated',
      clientArchive: 'Client archived',
      clientContactAdd: 'Contact added',
      clientContactArchive: 'Contact archived',
      clientContactRestore: 'Contact restored',
      clientContactSetPrimary: 'Primary contact changed',
      clientContactUpdate: 'Contact updated',
      clientCreate: 'Client created',
      clientLegalAdd: 'Legal entity added',
      clientLegalArchive: 'Legal entity archived',
      clientLegalRequisitesAdd: 'Requisites added',
      clientLegalRequisitesArchive: 'Requisites archived',
      clientLegalRequisitesRestore: 'Requisites restored',
      clientLegalRequisitesUpdate: 'Requisites updated',
      clientLegalRestore: 'Legal entity restored',
      clientLegalUpdate: 'Legal entity updated',
      clientRestore: 'Client restored',
      clientUpdate: 'Client updated',
      clientVersionRestore: 'Client restored from version',
      departmentArchive: 'Department archived',
      departmentCreate: 'Department created',
      departmentRestore: 'Department restored',
      departmentUpdate: 'Department updated',
      requestCarrierAdd: 'Carrier added to request',
      requestBoxRegroup: 'Box items regrouped',
      requestCarrierRemove: 'Carrier removed from request',
      requestCarrierUpdate: 'Carrier bid updated',
      requestChat: 'Request chat message',
      requestChatMarkUnread: 'Chat marked unread',
      requestChatUnmarkUnread: 'Chat unmarked',
      requestCreate: 'Request created',
      requestDelete: 'Request deleted',
      requestDraftIdempotentResolve: 'Draft create retry resolved by clientToken',
      requestChildRussiaCreated: 'Russia-rate request created from this one',
      requestTransition: 'Request status changed',
      requestUpdate: 'Request updated',
      requestWinner: 'Winner picked',
      roleArchive: 'Role archived',
      roleCreate: 'Role created',
      rolePermissionsSet: 'Role permissions changed',
      roleRestore: 'Role restored',
      roleUpdate: 'Role updated',
      russiaRequestCreate: 'Russia request created',
      russiaRequestDelete: 'Russia request deleted',
      russiaRequestTransition: 'Russia request status changed',
      russiaRequestUpdate: 'Russia request updated',
      russiaRequestChat: 'Russia request chat message',
      russiaRequestChatMarkUnread: 'Russia request chat marked unread',
      russiaRequestChatUnmarkUnread: 'Russia request chat unmarked',
      cargoCreate: 'Cargo created',
      cargoUpdate: 'Cargo updated',
      cargoDelete: 'Cargo deleted',
      cargoImport: 'Cargo imported',
      cargoDocumentAdd: 'Cargo payment document added',
      cargoDocumentDelete: 'Cargo payment document deleted',
      cargoPaymentSet: 'Client payment status set',
      cargoPaymentQuickSet: 'Client payment quick-set',
      cargoPaymentRequestStatus: 'Client payment status requested',
      cargoPaymentReview: 'Client payment reviewed',
      cargoProblemCreate: 'Cargo problem created',
      cargoProblemUpdate: 'Cargo problem updated',
      cargoProblemTransition: 'Cargo problem status changed',
      cargoProblemDelete: 'Cargo problem draft deleted',
      cargoProblemComment: 'Cargo problem comment added',
      cargoProblemFileUpload: 'Cargo problem file uploaded',
      cargoProblemAssigneeChange: 'Cargo problem assignee changed',
      cargoProblemAttachmentDelete: 'Cargo problem attachment deleted',
      cargoProblemReactionEscalate: 'Cargo problem reaction escalated',
      cargoClientTariffSet: 'Cargo client tariff set',
      cargoChinaPaymentSet: 'China payment set',
      cargoCarrierPaymentSet: 'Carrier payment status set',
      cargoDictionaryChange: 'Cargo dictionary changed',
      leadIntakeDutyToggle: 'Lead-intake duty toggled',
      callCenterAgentBind: 'Call-center source binding changed',
      clientLifecycleWorking: 'Client marked as working',
      packagingOptionArchive: 'Packaging archived',
      packagingOptionCreate: 'Packaging created',
      packagingOptionRestore: 'Packaging restored',
      packagingOptionUpdate: 'Packaging updated',
      russiaCarrierArchive: 'RU carrier archived',
      russiaCarrierCreate: 'RU carrier created',
      russiaCarrierRestore: 'RU carrier restored',
      russiaCarrierUpdate: 'RU carrier updated',
      roadOptionArchive: 'Road archived',
      roadOptionCreate: 'Road created',
      roadOptionRestore: 'Road restored',
      roadOptionUpdate: 'Road updated',
      transportTypeArchive: 'Transport type archived',
      transportTypeCreate: 'Transport type created',
      transportTypeRestore: 'Transport type restored',
      transportTypeUpdate: 'Transport type updated',
      userChangePassword: 'User password changed',
      userCreate: 'User created',
      userDeactivate: 'User deactivated',
      userReactivate: 'User reactivated',
      userResetPassword: 'User password reset',
      userSelfUpdate: 'Profile updated by user',
      userShiftToggle: 'Logist shift toggled',
      userUpdate: 'User updated',
    },
    fields: {
      action: 'Action',
      actor: 'Actor',
      reason: 'Reason',
      target: 'Target',
      time: 'Time',
    },
    filters: {
      action: 'Action',
      actor: 'Actor',
      actorPlaceholder: 'Actor (UUID)',
      all: 'All',
      allActions: 'All actions',
      dateFrom: 'From',
      dateFromPlaceholder: 'From (ISO)',
      dateTo: 'To',
      dateToPlaceholder: 'To (ISO)',
      targetId: 'Target ID',
      targetType: 'Target type',
    },
    loadMore: 'Load more',
    noDiff: 'No diff',
    title: 'Audit log',
  },
  departments: {
    actions: {
      create: 'Create department',
      unarchive: 'Unarchive',
    },
    columns: {
      name: 'Name',
      status: 'Status',
    },
    confirm: {
      archiveText:
        'Department "{{name}}" will no longer be available for assigning new employees. Existing employees keep their assignment.',
      archiveTitle: 'Archive department',
      restoreText: 'Department "{{name}}" will become available for employee assignment again.',
      restoreTitle: 'Unarchive department',
    },
    detail: {
      archivedSuffix: 'archived',
      back: '← Departments',
      employees: 'Employees: {{total}} (active: {{active}})',
      emptyEmployees: 'This department has no employees yet',
      loadEmployeesError: 'Could not load employees',
    },
    empty: 'No departments created yet',
    fields: {
      namePlaceholder: 'Department name',
    },
    modals: {
      createTitle: 'New department',
      renameTitle: 'Rename department',
    },
    title: 'Departments',
    toasts: {
      archived: 'Department archived',
      created: 'Department created',
      renamed: 'Department renamed',
      restored: 'Department restored',
    },
  },
  roles: {
    actions: {
      archiveSystem: 'Archive (system)',
      create: 'Create role',
    },
    columns: {
      identifier: 'Identifier',
      name: 'Name',
      status: 'Status',
      type: 'Type',
    },
    confirm: {
      archiveText:
        'Role "{{name}}" will be archived. Existing users stay attached, but new or edited users will not be able to receive this role.',
      archiveTitle: 'Archive role',
      restoreText: 'Role "{{name}}" will become available for user assignment again.',
      restoreTitle: 'Restore role',
    },
    empty: 'No roles created yet',
    fields: {
      description: 'Description',
      descriptionOptional: 'Description (optional)',
      displayName: 'Display name',
      identifier: 'Identifier',
      identifierCreate: 'Identifier (lowercase, no dots)',
      displayNamePlaceholder: 'Sales manager',
    },
    modals: {
      createTitle: 'New role',
    },
    tabs: {
      details: 'Details',
      permissions: 'Permissions',
      users: 'Users',
    },
    title: 'Roles',
    toasts: {
      archived: 'Role archived',
      created: 'Role created',
      permissionsUpdated: 'Permissions updated',
      restored: 'Role restored',
      updated: 'Role updated',
    },
    users: {
      empty: 'No users are assigned to this role.',
    },
  },
  users: {
    actions: {
      changePassword: 'Change password',
      create: 'Create user',
      reset: 'Reset',
    },
    confirm: {
      emailChangeConfirm: 'Change email',
      emailChangeText: 'Changing email will terminate all active user sessions. Continue?',
      emailChangeTitle: 'Confirm email change',
    },
    empty: 'No users',
    errors: {
      invalidPhone: 'Enter the number in international format, e.g. +79991234567',
    },
    fields: {
      currentPassword: 'Current password',
      department: 'Department',
      name: 'Name',
      newPassword: 'New password',
      password: 'Password',
      phone: 'Phone',
      role: 'Role',
      status: 'Status',
    },
    filters: {
      active: 'Active',
      inactive: 'Inactive',
    },
    modals: {
      changePasswordTitle: 'Change password',
      createTitle: 'New user',
      deactivateText: 'All active user sessions will be forcibly terminated.',
      deactivateTitle: 'Deactivate {{name}}',
      reactivateText: 'The user will be able to sign in again.',
      reactivateTitle: 'Reactivate {{name}}',
      resetPasswordTitle: 'Reset password {{name}}',
    },
    placeholders: {
      departmentLoading: 'Loading departments…',
      departmentSelect: 'Choose department',
      roleLoading: 'Loading roles…',
      roleSelect: 'Choose role',
      deactivationReason: 'Deactivation reason…',
    },
    password: {
      confirmNew: 'Confirm new password',
      confirmRequired: 'Confirm the new password',
      mismatch: 'Passwords do not match',
      newMustDiffer: 'New password must differ from the current one',
      requirement: 'Minimum 12 characters.',
      requirementWithDifference: 'Minimum 12 characters. Must differ from the current one.',
    },
    tabs: {
      actions: 'Actions',
      profile: 'Profile',
    },
    title: 'Users',
    toasts: {
      changePasswordSuccess: 'Password updated. Sign in again.',
      created: 'User created',
      deactivated: 'User deactivated',
      reactivated: 'User reactivated',
      resetPassword: 'Password reset. The user will see the "Password change required" badge.',
      saved: 'Changes saved',
    },
  },
  revenue: {
    title: 'Revenue',
    monthSelectLabel: 'Month',
    chart: {
      title: 'Cumulative revenue by day',
      empty: 'No data available to plot.',
    },
    kpi: {
      total: 'Monthly total',
      buyouts: 'Buyouts',
      foreignTrade: 'Foreign trade',
      logistics: 'Logistics',
    },
    comparison: {
      previousMonth: 'Previous month',
      previousYear: 'Previous year',
      selectedTotal: 'Current period',
      comparisonTotal: 'Comparison period',
      noData: 'No data for the comparable period.',
      sheetMissing: 'Comparison sheet not found.',
    },
    manager: {
      name: 'Manager',
      total: 'Total',
      buyouts: 'Buyouts',
      foreignTrade: 'Foreign trade',
      logistics: 'Logistics',
    },
    source: {
      label: 'Source:',
      warning:
        'External Google Sheets workbook. The sheet is shared by link — anyone with the link can see its contents.',
    },
    freshness: {
      fetchedAt: 'Updated {{time}}',
    },
    stale: {
      banner: 'Showing cached data',
      generic: 'unable to refresh.',
      upstream_timeout: 'the workbook did not respond in time.',
      upstream_5xx: 'the workbook is temporarily unavailable.',
      upstream_4xx: 'access to the workbook is denied.',
      parse_error: 'could not parse the workbook response.',
      network_error: 'network error while reaching the workbook.',
    },
    errors: {
      notConfiguredTitle: 'Source not configured',
      notConfiguredText:
        'The REVENUE_GOOGLE_SHEETS_SOURCE environment variable is not set. Contact an administrator.',
      sourceInvalidTitle: 'Invalid source configuration',
      sourceInvalidText:
        'REVENUE_GOOGLE_SHEETS_SOURCE does not look like a Google Sheets identifier.',
      sourceUnavailableTitle: 'Source unavailable',
      sourceUnavailableText: 'Could not fetch data from Google Sheets. Try refreshing.',
      invalidMonthTitle: 'Invalid month',
      invalidMonthText: 'Expected a YYYY-MM month key.',
      monthNotFoundTitle: 'Month not found',
      monthNotFoundText: 'No sheet was found in the source for the selected month.',
    },
  },
  crm: {
    requests: {
      title: 'Requests',
      empty: 'No linked requests',
      error: 'Could not load requests',
      slaLabel: 'SLA deadline',
      createAction: 'Create request',
    },
  },
  leads: {
    title: 'Leads',
    empty: 'No leads',
    actions: {
      create: 'New lead',
      loadMore: 'Load more',
      claim: 'Claim',
    },
    fields: {
      publicCode: 'Code',
      name: 'Name',
      phone: 'Phone',
      email: 'Email',
      comment: 'Comment',
      title: 'Subject',
      stage: 'Stage',
      manager: 'Manager',
      client: 'Client',
      status: 'Status',
      unassigned: 'Unassigned',
      intake: 'Call center',
    },
    filters: {
      search: {
        label: 'Search',
        placeholder: 'Subject, name, phone or code',
      },
      stage: {
        label: 'Stage',
        allLabel: 'All stages',
      },
      source: {
        label: 'Source',
        allLabel: 'All sources',
      },
      unassigned: {
        label: 'Assignment',
        all: 'All leads',
        only: 'Unassigned only',
      },
      archive: {
        label: 'Archive',
        active: 'Active',
        archived: 'Archived',
        all: 'All',
      },
      manager: {
        label: 'Manager',
        placeholder: 'All managers',
      },
    },
    intake: {
      title: 'Call-center submissions',
      empty: 'No call-center submissions',
      audioLink: 'Call recording',
      countTitle: 'Call-center submissions',
      source: 'Source',
      submittedBy: 'Submitted by',
      outcome: {
        CREATED_LEAD: 'Lead created',
        DUPLICATE_EXISTING_LEAD: 'Duplicate of existing lead',
        REJECTED_VALIDATION: 'Rejected: validation',
        REJECTED_SOURCE_DISABLED: 'Rejected: source disabled',
        REJECTED_NO_PROFILE: 'Rejected: no profile',
        INTERNAL_ERROR: 'Internal error',
      },
    },
    attention: {
      title: 'Needs attention',
      resolve: 'Resolve',
      empty: 'No attention items',
      callbackUrgent: 'Urgent callback',
    },
    stage: {
      new: 'New',
      inWork: 'In work',
      postponed: 'Postponed',
      awaitingPayment: 'Awaiting payment',
      returned: 'Returned',
      notSuitable: 'Not suitable',
      refusal: 'Refusal',
      paid: 'Paid',
    },
    stageControls: {
      pickTarget: 'Pick a stage',
      apply: 'Move',
      paidRequiresClient: 'Bind a client to move this lead to Paid.',
      terminalNote: 'Terminal stage — no transitions available.',
      archivedNote: 'Archived lead — transitions disabled.',
    },
    modals: {
      createTitle: 'New lead',
    },
    duplicates: {
      title: 'Other leads use this phone',
      body: 'Open an existing match if it is the same deal.',
      archived: 'archived',
      open: 'Open',
      createdPrefix: 'Created lead:',
    },
    phoneExists: {
      title: 'This phone is already in use',
      body: 'A record already uses this phone. Open it to continue.',
      bodyWithCode: 'Record {{code}} already uses this phone. Open it to continue.',
      openLead: 'Open existing lead',
      openClient: 'Open existing client',
    },
    client: {
      none: 'No client bound',
      bind: 'Bind client',
      unbind: 'Unbind',
      openClient: 'Open client card',
      searchPlaceholder: 'Search by name, phone, INN',
      empty: 'No matches',
    },
    match: {
      title: 'Bind lead to client',
      confirm: 'Bind',
      loading: 'Finding contacts…',
      loadError: 'Failed to load contacts',
      targetLabel: 'Client contact',
      contactNameLabel: 'New contact name',
      contactMatched: '{{name}} (identifier match)',
      createOption: 'Create a new contact',
    },
    admin: {
      title: 'Admin actions',
      pickManager: 'Pick a manager',
      reassign: 'Assign',
      noAssignableManagers: 'No eligible managers',
    },
    errors: {
      invalidPhone: 'Enter the number in international format, e.g. +79991234567',
    },
    toasts: {
      created: 'Lead created',
      stageChanged: 'Stage updated',
      clientBound: 'Client bound',
      clientUnbound: 'Client unbound',
      claimed: 'Lead claimed',
      archived: 'Lead archived',
      restored: 'Lead restored',
      reassigned: 'Manager reassigned',
    },
    activity: {
      feed: {
        title: 'Activity',
        empty: 'No activity yet',
        actorSystem: 'System',
        commentDeleted: '(comment deleted)',
        fileDeleted: '(file deleted)',
      },
      kind: {
        LEAD_CREATED: 'created the lead',
        LEAD_STAGE_CHANGED: 'changed the stage',
        LEAD_ASSIGNED: 'assigned a manager',
        LEAD_UNASSIGNED: 'removed the manager',
        LEAD_CLIENT_BOUND: 'bound a client',
        LEAD_CLIENT_UNBOUND: 'unbound the client',
        LEAD_TASK_CREATED: 'created a task',
        LEAD_TASK_COMPLETED: 'completed a task',
        LEAD_TASK_CANCELLED: 'cancelled a task',
        LEAD_TASK_REOPENED: 'reopened a task',
        LEAD_COMMENT_ADDED: 'added a comment',
        LEAD_COMMENT_EDITED: 'edited a comment',
        LEAD_ARCHIVED: 'archived the lead',
        LEAD_RESTORED: 'restored the lead',
        LEAD_REQUEST_LINKED: 'linked a request',
        LEAD_REQUEST_UNLINKED: 'unlinked a request',
        LEAD_FILE_UPLOADED: 'uploaded a file',
        LEAD_FILE_DELETED: 'deleted a file',
        LEAD_INTAKE_RECEIVED: 'received a call-center intake',
        CALL: 'logged a call',
        MESSAGE: 'logged a message',
      },
      comments: {
        title: 'Comments',
        empty: 'No comments',
        authorDeleted: '(author deleted)',
        composerPlaceholder: 'Add a comment…',
        composerAriaLabel: 'New comment',
        editAriaLabel: 'Edit comment',
        submit: 'Add',
        revisions_one: '{{count}} revision',
        revisions_few: '{{count}} revisions',
        revisions_many: '{{count}} revisions',
        revisions_other: '{{count}} revisions',
        errors: {
          tooLong: 'Comment must not exceed {{max}} characters',
        },
        toasts: {
          created: 'Comment added',
          updated: 'Comment updated',
        },
      },
      files: {
        title: 'Files',
        empty: 'No files',
        upload: 'Upload file',
        uploading: 'Uploading…',
        uploaderDeleted: '(uploader deleted)',
        usage: '{{count}} of {{max}} used',
        maxSize: 'Max {{size}}',
        capReached: 'This lead has reached its file limit.',
        confirmDelete: {
          title: 'Delete file?',
          text: '"{{name}}" will be deleted. This cannot be undone.',
        },
        errors: {
          tooLarge: 'File exceeds {{size}}',
          unsupportedMime: 'This file type is not supported',
          nameTooLong: 'File name exceeds {{max}} characters',
        },
        toasts: {
          uploaded: 'File uploaded',
          deleted: 'File deleted',
        },
      },
      note: {
        title: 'Note',
        placeholder: 'Write a note…',
        submit: 'Save',
        toasts: {
          created: 'Note added',
        },
      },
      communications: {
        callTitle: 'Log a call',
        messageTitle: 'Log a message',
        directionLabel: 'Direction',
        channelLabel: 'Channel',
        occurredAtLabel: 'Date and time',
        textLabel: 'Notes',
        textPlaceholder: 'What was discussed…',
        transcriptPlaceholder: 'Transcript not available yet',
        communicationDeleted: '(communication deleted)',
        submit: 'Save',
        direction: {
          INBOUND: 'Inbound',
          OUTBOUND: 'Outbound',
        },
        channel: {
          PHONE: 'Phone',
          WHATSAPP: 'WhatsApp',
          TELEGRAM: 'Telegram',
        },
        toasts: {
          created: 'Communication logged',
        },
      },
    },
    episode: {
      tabs: {
        feed: 'Feed',
        tasks: 'Tasks',
        tasksCount: 'Tasks ({{count}})',
        client: 'Client',
        requests: 'Requests',
        requestsCount: 'Requests ({{count}})',
      },
      clientSummary: {
        title: 'Client',
      },
      otherEpisodes: {
        title: 'Other client episodes',
        leadsTitle: 'Leads',
        dealsTitle: 'Deals',
        leadsEmpty: 'No other leads',
        dealsEmpty: 'No other deals',
      },
      capture: {
        note: 'Note',
        file: 'File',
        call: 'Call',
        message: 'Message',
      },
      upcoming: {
        title: 'Scheduled',
        empty: 'No scheduled tasks',
      },
      filters: {
        toggle: 'Filters',
        category: 'Type',
        searchLabel: 'Search',
        searchPlaceholder: 'Search the feed…',
      },
      feedTabs: {
        all: 'All',
        notes: 'Notes',
        system: 'System',
      },
    },
    tasks: {
      title: 'Tasks',
      empty: 'No open tasks',
      openCount_one: '{{count}} open',
      openCount_few: '{{count}} open',
      openCount_many: '{{count}} open',
      openCount_other: '{{count}} open',
      overdueCount: '{{count}} overdue',
      create: 'New task',
      noDue: 'No due date',
      autoHint: 'Automatic task — managed by the system',
      actions: {
        reschedule: 'Reschedule',
        complete: 'Complete',
        cancel: 'Cancel',
        reopen: 'Reopen',
      },
      cancelled: {
        title: 'Cancelled',
      },
      form: {
        createTitle: 'New task',
        editTitle: 'Reschedule task',
        titleLabel: 'Title',
        titlePlaceholder: 'What needs to be done?',
        dueLabel: 'Due date',
      },
      toasts: {
        created: 'Task created',
        rescheduled: 'Task updated',
        completed: 'Task completed',
      },
    },
    buckets: {
      all: 'All',
      unassigned: 'Unassigned',
      new: 'New',
      inWork: 'In work',
      closing: 'Closing',
      deal: 'Deals',
      postponed: 'Postponed',
      noClient: 'No client',
      closed: 'Closed',
    },
    list: {
      contact: 'Contact',
      company: 'Client',
      lead: 'Lead',
      noContact: 'Unnamed',
      noClientLinked: 'no client linked',
      noCompany: '—',
      tasksColumn: 'Tasks',
      noNextStep: 'No next step',
      unqualified: 'Not qualified',
      updated: 'Updated',
      totalCount_one: '{{count}} engagement',
      totalCount_few: '{{count}} engagements',
      totalCount_many: '{{count}} engagements',
      totalCount_other: '{{count}} engagements',
      score: 'Score',
      overdue: 'Overdue',
      resumeAt: 'Resume {{date}}',
      sortLabel: 'Order',
      sortRecent: 'Newest first',
      sortOldest: 'Oldest first',
      scoreLabel: 'Score',
      scoreAll: 'Any score',
      clientLabel: 'Client filter',
      clientActive: 'Client: {{name}}',
      clientClear: 'Clear client filter',
      filtersToggle: 'Filters',
    },
    card: {
      title: 'Lead',
      funnel: {
        new: 'New',
        inWork: 'In work',
        closing: 'Closing',
        deal: 'Deal',
      },
      inWorkLocked: 'Bind a client to take the lead into work',
      actions: {
        postpone: 'Postpone',
        refuse: 'Refuse',
        nonTarget: 'Not a target',
        backToWork: 'Back to work',
      },
      dealCode: 'Deal',
      score: {
        ariaLabel: 'Score {{score}} of 5',
        unrated: 'Not scored',
        qualifyPrompt: 'Qualify',
      },
      contact: 'Contact',
      unnamed: 'Unnamed',
      client: {
        title: 'Client',
      },
      clientActions: 'Client actions',
      titleEdit: {
        edit: 'Edit title',
        placeholder: 'Lead title',
        saved: 'Title updated',
      },
      contactActions: {
        call: 'Call',
        email: 'Email',
        telegram: 'Telegram — no data',
        whatsapp: 'WhatsApp — no data',
        max: 'MAX — no data',
      },
      banner: {
        postponed: 'Postponed · returns to work {{date}}',
        resume: 'Resume now',
        terminalRefused: 'Refused · read-only card',
        terminalNonTarget: 'Not a target · read-only card',
        deal: 'Deal {{dealCode}} · payment confirmed',
        dealNoCode: 'Deal · payment confirmed',
      },
      artifacts: {
        title: 'Artifacts',
        menuLabel: 'Add artifact',
        addFile: 'File',
        addProposal: 'Proposal',
        system: 'In system',
        empty: 'Nothing yet. Proposals and requests will appear automatically once created.',
        kind: {
          request: 'China request',
          'russia-request': 'Russia request',
          deal: 'Deal',
          proposal: 'Proposal',
        },
      },
    },
    checklist: {
      title: 'Closing checklist',
      progress: '{{done}}/{{total}}',
      optional: 'optional',
      confirmPaymentHint: 'Payment is confirmed by moving the stage to “Paid”',
      status: {
        TODO: 'To do',
        DONE: 'Done',
        SKIPPED: 'Skipped',
      },
      items: {
        contract_legal_entities_check: 'Check legal entities for the contract',
        spec: 'Specification',
        contract: 'Contract',
        issue_invoice: 'Issue invoice',
        send_invoice: 'Send invoice',
        receive_payment_slip: 'Receive payment slip',
        confirm_payment: 'Confirm payment',
      },
    },
  },
  deals: {
    title: 'Deals',
    empty: 'No deals',
    wonCount: 'Deals (won)',
    actions: {
      loadMore: 'Load more',
    },
    fields: {
      publicCode: 'Code',
      client: 'Client',
      manager: 'Manager',
      lead: 'Source lead',
      unlinked: 'Not linked',
      updated: 'Updated',
    },
  },
  myDay: {
    title: 'My day',
    timezone: 'Timezone: {{tz}}',
    empty: {
      title: 'No tasks',
      text: 'Tasks will appear here as they are assigned or come due.',
    },
    bucket: {
      overdue: 'Overdue',
      today: 'Today',
      upcoming: 'Upcoming',
      blocked: 'Blocked',
    },
    bucketEmpty: {
      overdue: 'No overdue tasks.',
      today: 'Nothing due today.',
      upcoming: 'No upcoming tasks.',
      blocked: 'No blocked tasks.',
    },
    row: {
      complete: 'Complete',
      completing: 'Saving…',
      stagePrimary: 'Stage',
      callbackUrgent: 'Urgent callback',
      openLeadAria: 'Open lead {{code}}',
    },
    requests: {
      title: 'Requests need action',
      loading: 'Loading requests…',
      empty: 'No requests need action.',
      error: {
        title: 'Could not load requests',
        text: 'Could not load the requests that need action. Try again.',
      },
      signal: {
        returned: 'Returned',
        slaHot: 'SLA due soon',
        readyToPick: 'Ready to pick winner',
        unreadChat: 'Unread chat',
      },
    },
  },
  callCenter: {
    intake: {
      title: 'New lead',
      fields: {
        phone: 'Phone',
        name: 'Client name',
        comment: 'Comment',
        audioUrl: 'Call recording link',
      },
      submit: 'Submit lead',
      success: 'Lead submitted. You can enter the next one.',
      logout: 'Sign out',
      noAccess: {
        title: 'No access',
        text: 'Your account does not have access to the lead intake form. Contact your administrator.',
      },
      errors: {
        invalidPhone: 'Invalid phone number',
        invalidAudioUrl: 'Invalid recording link (an https address is required)',
      },
    },
    selfService: {
      title: 'My leads',
      newLead: 'New lead',
      searchPlaceholder: 'Search by phone',
      filters: 'Filters',
      empty: 'You have not submitted any leads yet.',
      loadMore: 'Load more',
      loading: 'Loading…',
      submitSuccess: 'Lead submitted.',
      ownDuplicate: 'You have already submitted a lead with this phone number',
      columns: {
        phone: 'Phone',
        name: 'Client name',
        comment: 'Comment',
        audioUrl: 'Call recording',
        createdAt: 'Added at',
      },
      audioLink: 'Open recording',
      detail: {
        title: 'Lead',
        noComment: 'No comment',
        noAudio: 'No recording',
      },
      newLeadDrawer: {
        title: 'New lead',
      },
    },
  },
  callCenterAdmin: {
    pageTitle: 'Call center & manager duty',
    fields: {
      email: 'Email',
      source: 'Source',
    },
    placeholders: {
      sourceSelect: 'Select a source',
    },
    accounts: {
      title: 'Call-center accounts',
      create: 'Create account',
      empty: 'No call-center accounts yet.',
      colName: 'Name',
      colBinding: 'Source',
    },
    binding: {
      none: 'No binding',
      warning: 'Needs attention',
    },
    actions: {
      rebind: 'Change source',
    },
    create: {
      title: 'New call-center account',
    },
    rebind: {
      title: 'Change source binding',
    },
    duty: {
      title: 'Manager lead-intake duty',
      empty: 'No eligible managers.',
      emptyDutyWarning: 'No manager is on duty — new leads have nobody to route to.',
      on: 'On duty',
      off: 'Off duty',
      ariaOn: 'On duty — tap to go off duty',
      ariaOff: 'Off duty — tap to go on duty',
    },
    toasts: {
      created: 'Account created.',
      rebound: 'Source changed.',
    },
  },
  logisticsCalculations: {
    title: 'Waybills',
    loading: 'Loading…',
    error: 'Could not load calculations.',
    empty: 'No calculations yet.',
    statuses: { DRAFT: 'Draft', PUBLISHED: 'Published' },
    delete: {
      title: 'Delete draft {{code}}?',
      text: 'The draft is deleted for good. Its cargos can be included in another waybill again.',
      textRevoked:
        'This document was published and revoked before. It is deleted for good together with its view history; its cargos can be included in another waybill again.',
      done: 'Draft {{code}} deleted',
    },
    paymentLabels: {
      novat: 'No VAT',
      vatIncluded: 'VAT included',
      individual: 'Individual payment',
    },
    payableCurrencies: {
      RUB: 'Roubles',
      USD: 'Dollars',
      USD_RUB: 'Dollars + Roubles',
    },
    sections: {
      chinaLogistics: 'Logistics from China',
      chinaExpenses: 'China expenses',
      rfExpenses: 'Russia expenses',
      rfLogistics: 'Russia logistics',
    },
    units: {
      kg: 'kg',
      m3: 'm³',
      package: 'package',
      item: 'item',
      cargo: 'cargo',
      trip: 'trip',
      day: 'day',
      fixed: 'fixed',
      custom: 'Custom',
    },
    list: {
      number: 'Number',
      status: 'Status',
      client: 'Client',
      owner: 'Manager',
      cargos: 'Cargo',
      totalUsd: 'Total $',
      totalRub: 'Total ₽',
      views: 'Views',
      updated: 'Updated',
    },
    detail: {
      totalUsd: 'Total USD',
      totalRub: 'Total RUB',
      htmlViews: 'Page views',
    },
    document: {
      narrowNotice:
        'The document is laid out to A4 width, so filling it in needs a wider screen. Here it is read-only — its tables scroll sideways.',
    },
    actions: {
      create: 'Create waybill',
      cancel: 'Cancel',
      close: 'Close',
      creating: 'Creating…',
      createDraft: 'Create draft',
      save: 'Save',
      edit: 'Edit',
      publish: 'Publish',
      revoke: 'Revoke',
      delete: 'Delete',
      copyLink: 'Copy link',
      linkCopied: 'Link copied',
      downloadPdf: 'Download PDF',
      back: 'Back to list',
    },
    editor: {
      title: 'New calculation',
      editTitle: 'Edit {{code}}',
      createError: 'Could not create draft.',
      publishError: 'Could not publish the document.',
      publishNotReady: 'Remove the incomplete services left over from an earlier document version.',
      numberAfterSave: 'Number assigned after saving',
      validationError: 'Check the required fields and try again.',
      cargoHeld:
        'The cargo is already in calculation {{code}}. Remove its row or release it there.',
      cargoHeldUnknown:
        'The cargo is already in another calculation. Remove its row from this document.',
      prefill: {
        held: 'The cargo is already in calculation {{code}}, so the document opened without it.',
        unchecked: 'The cargo could not be checked. It can be added from the registry below.',
      },
      fields: {
        style: 'Document style',
        clientCompany: 'Company',
        clientContactName: 'Contact',
        clientContactPhone: 'Phone',
        payDueDate: 'Pay by',
        paymentLabel: 'Payment terms',
        paymentDivisor: 'Divisor',
        paymentDivisorHint: 'total / {{divisor}} = payable',
        payableCurrency: 'Payment currency',
        rubPerUsdRate: 'USD rate (₽ per 1 dollar)',
        rubPerCnyRate: 'CNY rate (₽ per 1 yuan)',
        managerName: 'Manager name',
        managerPhone: 'Manager phone',
        managerTelegram: 'Manager Telegram',
      },
      cargoPicker: {
        label: 'Add cargo from registry',
        placeholder: 'Enter article or name',
        choose: 'Choose cargo',
        add: 'Add',
        added: 'Cargo {{article}} added.',
        duplicate: 'Cargo {{article}} has already been added.',
        heldOption: 'in calculation {{code}}',
        heldMessage:
          'The cargo is already in calculation {{code}}. Release it there to add it here.',
      },
      cargo: {
        title: 'Cargo {{number}}',
        remove: 'Remove cargo',
        empty: 'Add at least one cargo.',
        article: 'Article',
        name: 'Name',
        seats: 'Packages',
        weight: 'Weight',
        volume: 'Volume',
        transportType: 'Transport',
        departedAt: 'Departed',
        arrivedAt: 'Arrived',
        invalidDateTime: 'Enter a valid date and time.',
      },
      cost: {
        amount: '{{column}}: amount',
        caption: '{{column}}: note',
        currency: '{{column}}: currency',
        hints: {
          transport: '250 kg * $4.5 = $1 125',
          insurance: '1% of $2 300',
          extra: 'What the cost is for',
        },
        addExtra: 'Add extra cost',
        removeExtra: 'Remove extra cost {{index}}',
        removeLine: 'Remove service',
      },
    },
  },
  logisticsCalculationsPublic: {
    loading: 'Loading calculation…',
    notFound: {
      title: 'Calculation not found',
      body: 'This link is invalid or no longer available.',
    },
    downloadPdf: 'Download PDF',
  },
  validation: {
    required: 'Required field',
  },
} as const satisfies typeof ru;
